import React, { useState, useMemo, useRef } from "react";
import KPIStrip from "../components/KPIStrip";
import WaterUsageFilters, {
  DEFAULT_FILTERS,
} from "../components/WaterUsageFilters";
import WeeklyTrendChart from "../components/WeeklyTrendChart";
import ReactApexChart from "react-apexcharts";
import { useTheme } from "../context/ThemeContext";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  getAccountMeterCategoryList,
  getWaterUsageKPI,
  getMetersByFlow,
  // getDailyHourlyTrend,
  getWaterUsageBreakdown,
  getWaterUsageTrendPlots,
  getDailyHourlyTrendData, // <-- Added
  getWeeklyTrendData,
  getWeeklyTrendHistorical,
  getCustomWeeklyTrend,
} from "../services";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { Dropdown, Spin } from "antd";
import { useDistrict } from "../utils/DistrictContext";
import {
  Activity,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Download,
  ArrowLeft,
} from "lucide-react";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";

const dailyAvg = [
  { x: "Weekday", y: 5600 },
  { x: "Weekend", y: 6800 },
  { x: "Monday", y: 4200 },
  { x: "Tuesday", y: 5100 },
  { x: "Wednesday", y: 4800 },
  { x: "Thursday", y: 5300 },
  { x: "Friday", y: 6200 },
  { x: "Saturday", y: 7100 },
  { x: "Sunday", y: 3900 },
];

const hourlyAvg = [
  { x: "12am", y: 10500 },
  { x: "1am", y: 9800 },
  { x: "2am", y: 9500 },
  { x: "3am", y: 9700 },
  { x: "4am", y: 10200 },
  { x: "5am", y: 12000 },
  { x: "6am", y: 14500 },
  { x: "7am", y: 16200 },
  { x: "8am", y: 15800 },
  { x: "9am", y: 14200 },
  { x: "10am", y: 13500 },
  { x: "11am", y: 13000 },
  { x: "12pm", y: 12800 },
  { x: "1pm", y: 12500 },
  { x: "2pm", y: 12300 },
  { x: "3pm", y: 13800 },
  { x: "4pm", y: 17500 },
  { x: "5pm", y: 21800 },
  { x: "6pm", y: 23200 },
  { x: "7pm", y: 21500 },
  { x: "8pm", y: 18200 },
  { x: "9pm", y: 15500 },
  { x: "10pm", y: 13200 },
  { x: "11pm", y: 11500 },
];

// =========================
// CHART BASE CONFIG
// =========================

const zoomToolbar = {
  show: true,
  tools: {
    download: false,
    selection: true,
    zoom: true,
    zoomin: true,
    zoomout: true,
    pan: true,
    reset: true,
  },
  autoSelected: "zoom",
};

const zoomConfig = {
  enabled: true,
  type: "x",
  autoScaleYaxis: true,
  allowMouseWheelZoom: false,
};

const themedChartBase = {
  chart: {
    foreColor: "#8ba1c7",
    toolbar: zoomToolbar,
    zoom: zoomConfig,
  },

  grid: {
    borderColor: "#233d5e",
    strokeDashArray: 3,
  },

  tooltip: {
    theme: "dark",
  },

  legend: {
    labels: {
      colors: "#e6f0ff",
    },
  },
};

// =========================
// COMPONENT
// =========================

const WaterUsageDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const meterIdFromDigitalTwin = searchParams.get("meter_id");
  const startDateFromDigitalTwin = searchParams.get("start_date");
  const endDateFromDigitalTwin = searchParams.get("end_date");
  const fromPage = searchParams.get("from") || location.state?.from;

  const isFromDigitalTwin = fromPage === "digitalTwin";
  const [weeklyAnalytics, setWeeklyAnalytics] = useState([]);
  const [customWeeklySeries, setCustomWeeklySeries] = useState([]);

  const initialFilters = useMemo(() => {
    if (meterIdFromDigitalTwin) {
      return {
        ...DEFAULT_FILTERS,
        mode: "Custom",
        chip: "Custom",
        start: startDateFromDigitalTwin || DEFAULT_FILTERS.start,
        end: endDateFromDigitalTwin || DEFAULT_FILTERS.end,
        meter: [meterIdFromDigitalTwin],
        account: [],
        category: [],
      };
    }

    return DEFAULT_FILTERS;
  }, [
    meterIdFromDigitalTwin,
    startDateFromDigitalTwin,
    endDateFromDigitalTwin,
  ]);

  const [filters, setFilters] = useState(initialFilters);
  const [trendPlotsMatrix, setTrendPlotsMatrix] = useState([]);
  const [kpiMatrix, setKpiMatrix] = useState([]);
  const [aggregateSeries, setAggregateSeries] = useState([]);
  const aggregateChartRef = useRef(null);
  const dailyHourlyChartRef = useRef(null);
  const breakdownChartRef = useRef(null);
  const weeklyChartRef = useRef(null);
  const { selectedDistrict } = useDistrict();
  const [draftMeterStatus, setDraftMeterStatus] = useState("active");
  const [appliedMeterStatus, setAppliedMeterStatus] = useState("active");
  const [flowInterval, setFlowInterval] = useState(
    initialFilters.interval || "Day",
  );
  // const [selectedBreakdownTooltip, setSelectedBreakdownTooltip] = useState(null);
  const [trendAnalyticsMatrix, setTrendAnalyticsMatrix] = useState({
    daily: [],
    hourly: [],
    weekly: [],
  });
  const [breakdownView, setBreakdownView] = useState("category");
  const [hourlyAnalyticsRoot, setHourlyAnalyticsRoot] = useState(null);
  const trendAnalyticsRequestKeyRef = useRef("");

  // trendAnalyticsRequestKeyRef.current = "";
  const districtId =
    typeof selectedDistrict === "object"
      ? selectedDistrict?.selectedDistrict ||
        selectedDistrict?.district ||
        selectedDistrict?.id ||
        ""
      : selectedDistrict || "";

  const [isLoading, setIsLoading] = useState(true);

  const [aggType, setAggType] = useState("bar");
  const [isAggregateWheelZoomActive, setIsAggregateWheelZoomActive] =
    useState(false);
  const [isWeeklyWheelZoomActive, setIsWeeklyWheelZoomActive] = useState(false);
  const [isHourlyWheelZoomActive, setIsHourlyWheelZoomActive] = useState(false);
  const [dailyType, setDailyType] = useState("bar");

  const [hourlyType, setHourlyType] = useState("line");
  const [acctType, setAcctType] = useState("bar");
  const [activeCategory, setActiveCategory] = useState(null);
  const { colors, theme } = useTheme();
  // Ensure these exist right under your other useState/useRef declarations!
  const [loadingDailyHourly, setLoadingDailyHourly] = useState(false);
  const [loadingWeekly, setLoadingWeekly] = useState(false);

  const dailyHourlyRequestKeyRef = useRef("");
  const weeklyRequestKeyRef = useRef("");
  const cardStyle = {
    backgroundColor: colors.cardBg,
    borderColor: colors.border,
  };

  const [optionFilters, setOptionFilters] = useState({
    account: [],
    meter: meterIdFromDigitalTwin ? [meterIdFromDigitalTwin] : [],
    category: [],
  });
  // Inside WaterUsageDetail component
  const handleAddCustomWeek = async (dateString) => {
    try {
      const payload = {
        week_date: dateString,
        accounts: filters.account || [],
        meter_ids: filters.meter || [],
        codes: filters.category || [],
        meter_status: appliedMeterStatus || "all"
      };

      const res = await getCustomWeeklyTrend(payload);
      
      // Safely grab the response data
      const responseData = res?.data?.data || res?.data || res || {};

      // 1. Dig into the nested structure you provided: weekly_trends[0].daily[0]
      const customWeekRoot = responseData?.weekly_trends?.[0];
      const customWeekData = customWeekRoot?.daily?.[0];

      if (customWeekData && customWeekData.days) {
        // 2. Use the exact string the API gives you (e.g., "Dec 29, 2025 - Jan 4, 2026")
        const newSeriesName = customWeekData.week_range || `Week of ${dateString}`;
        
        // 3. Map the days array into a flat array of numbers for ApexCharts
        const newSeriesData = customWeekData.days.map((day) => Number(day.total_flow || 0));

        const customSeries = {
          name: newSeriesName,
          data: newSeriesData,
          isCustom: true,
        };

        // 4. Update the state to render the new line
        setCustomWeeklySeries((prev) => {
          // Optional: Prevent duplicate weeks from being added
          if (prev.some(series => series.name === newSeriesName)) {
             toast.info("That week is already on the chart.");
             return prev;
          }
          return [...prev, customSeries];
        });
        
        toast.success(`Added custom week: ${newSeriesName}`);
      } else {
        toast.warning(`No data found for the week of ${dateString}`);
      }
      
    } catch (error) {
      console.error("Error fetching custom week:", error);
      toast.error("Failed to fetch custom week data.");
    }
  };
  const innerCardStyle = {
    backgroundColor: colors.background,
    borderColor: colors.border,
  };

  const mutedTextStyle = {
    color: colors.textSecondary,
  };

  const mainTextStyle = {
    color: colors.text,
  };
  const showMeterAllToggle = useMemo(() => {
    const isFb151 = districtId?.toUpperCase() === "FB151";

    const hasOverridingFilters =
      (filters.account?.length || 0) > 0 ||
      (filters.meter?.length || 0) > 0 ||
      (filters.category?.length || 0) > 0;

    if (isFb151 || hasOverridingFilters) {
      return false;
    }
    return true;
  }, [districtId, filters.account, filters.meter, filters.category]); // Added filters.account dependency
  const themedChartBase = useMemo(() => {
    const isLightTheme = theme === "white" || theme === "light";

    return {
      chart: {
        foreColor: colors.textSecondary,
        toolbar: zoomToolbar,
        zoom: zoomConfig,
      },

      grid: {
        borderColor: colors.border,
        strokeDashArray: 3,
      },

      tooltip: {
        theme: isLightTheme ? "light" : "dark",
      },

      legend: {
        labels: {
          colors: colors.text,
        },
      },
    };
  }, [colors, theme]);

  const [dhTab, setDhTab] = useState("daily");

  const [dailyAverageSeries, setDailyAverageSeries] = useState([]);
  const [hourlyAverageSeries, setHourlyAverageSeries] = useState([]);
  const [hourlyAnalyticsRoots, setHourlyAnalyticsRoots] = useState([]);
  const [dailyAverageDetails, setDailyAverageDetails] = useState({});
  const [hourlyAverageDetails, setHourlyAverageDetails] = useState({});

  const [hourlyDay, setHourlyDay] = useState("Monday");
  const [weeklyHistorical, setWeeklyHistorical] = useState([]);

  const [sort, setSort] = useState({
    key: "flow",
    direction: "desc",
  });

  //   pagination

  const [meterPage, setMeterPage] = useState(1);
  const [meterPageSize, setMeterPageSize] = useState(10);
  const [meterSearch, setMeterSearch] = useState("");

  const [options, setOptions] = useState({
    accounts: [],
    meters: [],
    categories: [],
  });
  const [meterRankings, setMeterRankings] = useState([]);
  const [meterByFlowMatrix, setMeterByFlowMatrix] = useState([]);
  const [loadingRankings, setLoadingRankings] = useState(false);

  const formatToK = (value) => {
    const num = Number(value || 0);

    if (num >= 1000) {
      return `${Math.round(num / 1000).toLocaleString("en-US")}K`;
    }

    return num.toLocaleString("en-US");
  };

  const allDropdownOptionsRef = useRef({
    accounts: [],
    meters: [],
    categories: [],
  });

  useEffect(() => {
    if (isFromDigitalTwin && meterIdFromDigitalTwin) return;

    setOptionFilters({
      account: [],
      meter: [],
      category: [],
    });
  }, [districtId, isFromDigitalTwin, meterIdFromDigitalTwin]);

  useEffect(() => {
    const fetchHistoricalData = async () => {
      try {
        const payload = {
          accounts: filters.account,
          meter_ids: filters.meter,
          codes: filters.category,
          option: getKpiOption(filters),
          start_date: filters.start,
          end_date: filters.end,
        };

        const res = await getWeeklyTrendHistorical(payload);

        if (res?.data?.weekly_trends) {
          const historicalSeries = res.data.weekly_trends.flatMap((root) =>
            (root.daily || []).map((week) => {
              let seriesName = week.week_range;
              let bracketText = week.week_place; // 🎯 Capture the bracket text

              // Normalize names and swap bracket text to match the chart component's expectations
              if (
                week.week_place === "Same Week Last Year" ||
                week.week_range?.toLowerCase().includes("last year")
              ) {
                seriesName = "Same Week Last Year";
                bracketText = week.week_range;
              } else if (
                week.week_place === "Same Week 2 Years Ago" ||
                week.week_range?.toLowerCase().includes("2 years ago")
              ) {
                seriesName = "Same Week 2 Years Ago";
                bracketText = week.week_range;
              } else if (
                week.week_place === "4 Yr Peak" ||
                week.week_range === "Historical Max"
              ) {
                seriesName = "Historical Max";
                bracketText = week.week_place || "4 Yr Peak";
              }

              return {
                name: seriesName,
                date_range: week.week_range,
                week_place: bracketText, // 🎯 Inject the bracket text into the series object
                data: week.days.map((day) => Number(day.total_flow || 0)),
              };
            }),
          );
          setWeeklyHistorical(historicalSeries);
        }
      } catch (error) {
        console.error("Historical API Error:", error);
      }
    };

    fetchHistoricalData();
  }, [districtId, filters]);

  const [breakdownData, setBreakdownData] = useState({
    topFlowTitle: "Top Accounts — Total Flow In Range",
    categoryBreakdown: [],
    meterBreakdown: [],
    accountBreakdown: [],
    liveTopAccounts: [],
    totalFlowGallons: 0,
  });
  const [breakdownMatrix, setBreakdownMatrix] = useState({
    breakdown: [],
    accountFlow: [],
  });

  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  const selectedBreakdownItems = useMemo(() => {
    if (breakdownView === "meter") {
      return breakdownData.meterBreakdown || [];
    }

    if (breakdownView === "account") {
      return breakdownData.accountBreakdown || [];
    }

    return breakdownData.categoryBreakdown || [];
  }, [breakdownView, breakdownData]);

  const categoryBreakdown = selectedBreakdownItems;

  const liveTopAccounts = breakdownData.liveTopAccounts;
  const totalFlowGallons = breakdownData.totalFlowGallons;

  const breakdownTitle =
    breakdownView === "meter"
      ? "By Meters"
      : breakdownView === "account"
        ? "By Accounts"
        : "By Category";

  const topFlowTitle = breakdownData.topFlowTitle;

  const [kpiData, setKpiData] = useState(null);
  const [loadingKpis, setLoadingKpis] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [meterAll, setMeterAll] = useState(false);
  const [chartData, setChartData] = useState({
    aggregate: [],
    dailyAvg: [],
    hourlyAvg: [],
    weeklyTrend: [],
  });
  const AVG_CHART_COLORS = {
    dayBar: "#2dd4bf", // Monday - Sunday bar color
    groupBar: "#b8e986", // Weekday + Weekend bar color

    dayLine: "#12bfc4", // Monday - Sunday line
    weekdayLine: "#b8e986", // Weekday line color
    weekendLine: "#b8e986", // Weekend line color

    groupLabel: "#b8e986",
  };

  const isWeekGroupLabel = (label) =>
    ["Weekday", "Weekend", "Weekdays", "Weekends"].includes(label);

  const [loadingCharts, setLoadingCharts] = useState(false);
  const [loadingTrendPlots, setLoadingTrendPlots] = useState(false);
  const [loadingTrendAnalytics, setLoadingTrendAnalytics] = useState(false);
  const selectedAccountCount = filters.account?.length || 0;
  const selectedMeterCount = filters.meter?.length || 0;
  const selectedCategoryCount = filters.category?.length || 0;

  const isFilteredView =
    selectedAccountCount > 0 ||
    selectedMeterCount > 0 ||
    selectedCategoryCount > 0;

  // const activeKpis = isFilteredView ? filteredKpis : kpis;

  const filteredText = [
    selectedAccountCount > 0 ? `${selectedAccountCount} account` : null,
    selectedMeterCount > 0 ? `${selectedMeterCount} meter` : null,
    selectedCategoryCount > 0 ? `${selectedCategoryCount} category` : null,
  ]
    .filter(Boolean)
    .join(" / ");

  const clearFilters = () => {
    setFilters({
      ...filters,
      account: [],
      meter: [],
      category: [],
    });
  };

  const kpiRequestKeyRef = useRef("");
  const rankingRequestKeyRef = useRef("");
  const optionsRequestKeyRef = useRef("");
  const trendRequestKeyRef = useRef("");
  const breakdownRequestKeyRef = useRef("");
  const previousDistrictRef = useRef(null);

  const getFilterKey = (filters, districtId, status = appliedMeterStatus) => {
    return JSON.stringify({
      districtId,
      meter_status: status,
      mode: filters.mode,
      chip: filters.chip,
      start: filters.start,
      end: filters.end,
      interval: filters.interval,
      account: filters.account || [],
      meter: filters.meter || [],
      category: filters.category || [],
    });
  };

  const removeApexDefaultTitle = (chartContextOrElement) => {
    const chartEl = chartContextOrElement?.el || chartContextOrElement;

    if (!chartEl) return;

    // Remove SVG <title> tags
    chartEl.querySelectorAll("svg title, title").forEach((title) => {
      title.remove();
    });

    // Remove native browser title attributes also
    chartEl.querySelectorAll("[title]").forEach((el) => {
      el.removeAttribute("title");
    });

    // Remove aria-label because some browsers show accessibility text
    chartEl
      .querySelectorAll(".apexcharts-pie-series path, .apexcharts-series path")
      .forEach((el) => {
        el.removeAttribute("title");
        el.removeAttribute("aria-label");
      });
  };

  const parseLastCommTime = (value) => {
    if (!value) return 0;

    // Handles:
    // "Tuesday, May 19, 2026 2:44 AM"
    // "2026-05-19 02:44"
    const cleanedValue = String(value)
      .replace(/^[A-Za-z]+,\s*/, "")
      .trim();

    const parsedTime = new Date(cleanedValue).getTime();

    return Number.isNaN(parsedTime) ? 0 : parsedTime;
  };

  const sortedMeters = useMemo(() => {
    const searchText = meterSearch.trim().toLowerCase();

    let data = [...meterRankings];

    if (searchText) {
      data = data.filter((item) => {
        const searchableText = [
          item.meter,
          item.meter_id,
          item.code_name,
          item.category,
          item.account_name,
          item.last_comm_time,
          item.flow,
        ]
          .map((value) => String(value ?? "").toLowerCase())
          .join(" ");

        return searchableText.includes(searchText);
      });
    }

    if (sort.key) {
      data.sort((a, b) => {
        const aValue = a[sort.key];
        const bValue = b[sort.key];

        // Special sorting for Last Comm date/time
        if (sort.key === "last_comm_time") {
          const aTime = parseLastCommTime(aValue);
          const bTime = parseLastCommTime(bValue);

          return sort.direction === "asc" ? aTime - bTime : bTime - aTime;
        }

        const aNum = Number(aValue);
        const bNum = Number(bValue);

        const bothNumbers =
          aValue !== null &&
          aValue !== undefined &&
          bValue !== null &&
          bValue !== undefined &&
          !Number.isNaN(aNum) &&
          !Number.isNaN(bNum);

        if (bothNumbers) {
          return sort.direction === "asc" ? aNum - bNum : bNum - aNum;
        }

        return sort.direction === "asc"
          ? String(aValue || "").localeCompare(String(bValue || ""))
          : String(bValue || "").localeCompare(String(aValue || ""));
      });
    }

    return data;
  }, [meterRankings, sort, meterSearch]);

  const meterTotalPages = Math.ceil(sortedMeters.length / meterPageSize);

  const meterStartIndex = (meterPage - 1) * meterPageSize;

  const paginatedMeters = sortedMeters.slice(
    meterStartIndex,
    meterStartIndex + meterPageSize,
  );

  const shouldSendAsCustom = (filters) => {
    return filters.mode === "Custom" || filters.chip === "YTD";
  };

  // filter
  const getTrendPlotsOption = (filters) => {
    if (shouldSendAsCustom(filters)) {
      return "Custom";
    }

    switch (filters.chip) {
      case "D":
        return "Past Day";
      case "W":
        return "Past Week";
      case "1M":
        return "Past Month";
      case "3M":
        return "Past 3 Months";
      case "6M":
        return "Past 6 Months";
      case "1Y":
        return "Past Year";
      default:
        return "Past Month";
    }
  };

  const getTrendPlotsInterval = (interval) => {
    if (interval === "Hour") return "Hours";
    if (interval === "Day") return "Days";
    if (interval === "Week") return "Weeks";

    if (interval === "Hours") return "Hours";
    if (interval === "Days") return "Days";
    if (interval === "Weeks") return "Weeks";

    return interval || "Days";
  };

  const parseTrendFlowTime = (flowTime) => {
    if (!flowTime) return null;

    const date = new Date(flowTime);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date.getTime();
  };

  const formatAggregateXAxisLabel = (timestamp, flowTime = "") => {
    const date = new Date(Number(timestamp));

    if (Number.isNaN(date.getTime())) {
      return flowTime || "";
    }

    const normalizedInterval = getTrendPlotsInterval(flowInterval);

    if (normalizedInterval === "Hours") {
      return date.toLocaleString("en-US", {
        month: "short",
        day: "2-digit",
        hour: "numeric",
      });
    }

    return date.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
    });
  };

  // end filter

  //   useEffect(() => {
  //   if (!meterIdFromDigitalTwin) return;

  //   setFilters((prev) => ({
  //     ...prev,
  //     mode: "Custom",
  //     chip: "Custom",
  //     start: startDateFromDigitalTwin || prev.start,
  //     end: endDateFromDigitalTwin || prev.end,
  //     meter: [meterIdFromDigitalTwin],
  //     account: [],
  //     category: [],
  //   }));

  //   setOptionFilters((prev) => ({
  //     ...prev,
  //     meter: [meterIdFromDigitalTwin],
  //     account: [],
  //     category: [],
  //   }));

  //   setAppliedMeterStatus(draftMeterStatus);
  // }, [meterIdFromDigitalTwin, startDateFromDigitalTwin, endDateFromDigitalTwin]);

  // Trend plots filter logic
  useEffect(() => {
    const fetchTrendPlots = async () => {
      if (!districtId) return;

      const option = getTrendPlotsOption(filters);

      if (option === "Custom" && (!filters.start || !filters.end)) {
        setLoadingTrendPlots(false);

        setChartData((prev) => ({
          ...prev,
          aggregate: [],
        }));

        setAggregateSeries([]);
        setTrendPlotsMatrix([]);
        return;
      }

      const requestKey = JSON.stringify({
        districtId,
        meter_status: appliedMeterStatus,
        option,
        start: filters.start,
        end: filters.end,
        interval: flowInterval,
        accounts: filters.account || [],
        meter_ids: filters.meter || [],
        codes: filters.category || [],
      });

      if (trendRequestKeyRef.current === requestKey) {
        return;
      }

      trendRequestKeyRef.current = requestKey;

      try {
        setLoadingTrendPlots(true);

        const payload = {
          accounts: filters.account || [],
          meter_ids: filters.meter || [],
          codes: filters.category || [],
          option,
          interval: getTrendPlotsInterval(flowInterval),
          meter_status: appliedMeterStatus,
        };

        if (option === "Custom") {
          payload.start_date = filters.start;
          payload.end_date = filters.end;
        }

        const res = await getWaterUsageTrendPlots(payload);

        const apiData = res?.data?.data || res?.data || res || {};

        const plots = apiData?.plots || [];
        const matrix = apiData?.plots_matrix || [];

        const dateMap = new Map();

        plots.forEach((plot) => {
          (plot?.data || []).forEach((item) => {
            const timestamp = parseTrendFlowTime(item.flow_time);

            if (timestamp !== null) {
              dateMap.set(timestamp, {
                x: String(timestamp), // unique category key
                timestamp,
                label: item.flow_time,
                xLabel: formatAggregateXAxisLabel(timestamp, item.flow_time),
              });
            }
          });
        });

        const aggregateDates = Array.from(dateMap.values()).sort(
          (a, b) => a.timestamp - b.timestamp,
        );

        const getTrendPlotSeriesName = (plot, index) => {
          // If no Account / Meter / Category filter selected,
          // show district-level name in graph legend and tooltip.
          if (!isFilteredView) {
            return "District Aggregate Flow";
          }

          if (plot?.meter_id) {
            return (
              plot?.meter_unique_name || plot?.meter_name || plot?.meter_id
            );
          }

          if (plot?.code_name) {
            return plot.code_name;
          }

          if (plot?.account_name) {
            return plot.account_name;
          }

          if (plot?.code) {
            return plot.code;
          }

          return "Flow";
        };

        const mappedSeries = plots
          .map((plot, index) => {
            const seriesName = getTrendPlotSeriesName(plot, index);

            const flowMap = new Map();

            (plot?.data || []).forEach((item) => {
              const timestamp = parseTrendFlowTime(item.flow_time);

              if (timestamp !== null) {
                flowMap.set(timestamp, {
                  flow: Number(item.flow ?? 0),
                  flowTime: item.flow_time,
                });
              }
            });

            return {
              name: seriesName,
              data: aggregateDates.map((dateItem) => {
                // dateItem.x is string, but flowMap key is number timestamp
                const matched = flowMap.get(dateItem.timestamp);

                const actualFlow = Number(matched?.flow ?? 0);

return {
  x: dateItem.x,

   y: actualFlow,
  actualFlow,

  timestamp: dateItem.timestamp,
  flowTime: matched?.flowTime || dateItem.label,

  seriesName,
  accountName: plot?.account_name || "",
  meterId: plot?.meter_id || "",
  code: plot?.code || "",
  codeName: plot?.code_name || "",
};
              }),
            };
          })
          // Keep zero-value series also.
          // Only remove series that have no date points at all.
          .filter((seriesItem) => seriesItem.data.length > 0);

        setChartData((prev) => ({
          ...prev,
          aggregate: aggregateDates,
        }));

        setAggregateSeries(mappedSeries);
        setTrendPlotsMatrix(matrix);
      } catch (error) {
        console.error(
          "Trend Plots API Error:",
          error.response?.data || error.message,
        );

        setChartData((prev) => ({
          ...prev,
          aggregate: [],
        }));

        setTrendPlotsMatrix([]);
      } finally {
        setLoadingTrendPlots(false);
      }
    };

    fetchTrendPlots();
  }, [
    districtId,
    appliedMeterStatus,
    filters.mode,
    filters.chip,
    filters.start,
    filters.end,
    flowInterval,
    filters.account,
    filters.meter,
    filters.category,
    isFilteredView,
  ]);

  const downloadTrendPlotsCSV = () => {
    if (!trendPlotsMatrix || trendPlotsMatrix.length === 0) {
      return;
    }

    const csvContent = trendPlotsMatrix
      .map((row) =>
        row
          .map((cell) => {
            const value = cell ?? "";
            const escapedValue = String(value).replace(/"/g, '""');
            return `"${escapedValue}"`;
          })
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", "water-usage-trend-plots.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const downloadTrendPlotsPNG = async () => {
    await downloadElementAsPNG(
      aggregateChartRef,
      "water-usage-trend-plots.png",
    );
  };

  const hasAggregateChartData =
    aggregateSeries.length > 0 &&
    aggregateSeries.some(
      (seriesItem) =>
        Array.isArray(seriesItem.data) && seriesItem.data.length > 0,
    );

  // end

  useEffect(() => {
    setMeterPage(1);
  }, [sort, filters, meterSearch]);

  const onSortClick = (key) => {
    if (key === "rank") return;

    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));

    setMeterPage(1);
  };

  const SortIcon = ({ columnKey }) => {
    if (sort.key !== columnKey) {
      return (
        <ArrowUpDown className="w-4 h-4" style={{ color: colors.primary }} />
      );
    }

    return sort.direction === "asc" ? (
      <ArrowUp className="w-4 h-4" style={{ color: colors.primary }} />
    ) : (
      <ArrowDown className="w-4 h-4" style={{ color: colors.primary }} />
    );
  };

  const sortClass = (key) =>
    "sortable" + (sort.key === key ? ` sort-${sort.dir}` : "");

  const sortArrow = (key) =>
    sort.key === key ? (sort.dir === "asc" ? "▲" : "▼") : "▲▼";

  // tooltip

  const buildAverageOnlyTooltip = ({
    dataPointIndex,
    w,
    titlePrefix = "",
    footerLabel = "Average Flow",
    sourcePoints = [],
  }) => {
    const isLightTheme = theme === "white" || theme === "light";

    const tooltipBg = isLightTheme ? "#ffffff" : colors.background;
    const tooltipText = colors.text;
    const tooltipMuted = colors.textSecondary;
    const tooltipBorder = colors.border || `${colors.primary}40`;
    const tooltipShadow = isLightTheme
      ? "0 8px 24px rgba(15, 23, 42, 0.12)"
      : "0 8px 24px rgba(0, 0, 0, 0.38)";

    const xLabels = w?.globals?.labels || w?.config?.xaxis?.categories || [];

    const sourcePoint = sourcePoints?.[dataPointIndex];

    const title =
      sourcePoint?.x ||
      xLabels?.[dataPointIndex] ||
      w?.globals?.categoryLabels?.[dataPointIndex] ||
      "";

    const configSeries = w?.config?.series || [];

    const matchedPoint = configSeries
      .map((seriesItem) => seriesItem?.data?.[dataPointIndex])
      .find((point) => {
        if (point === null || point === undefined) return false;

        if (typeof point === "object") {
          return point.y !== null && point.y !== undefined;
        }

        return point !== null && point !== undefined;
      });

   const value =
  sourcePoint?.actualFlow !== null && sourcePoint?.actualFlow !== undefined
    ? Number(sourcePoint.actualFlow)
    : typeof matchedPoint === "object"
      ? Number(matchedPoint?.actualFlow ?? matchedPoint?.y ?? 0)
      : Number(matchedPoint ?? 0);

    return `
    <div style="
      background: ${tooltipBg};
      border: 1px solid ${tooltipBorder};
      border-radius: 0px;
      padding: 10px 12px;
      min-width: 280px;
      max-width: 380px;
      color: ${tooltipText};
      box-shadow: ${tooltipShadow};
      font-family: inherit;
    ">
      <div style="
        font-size: 13px;
        font-weight: 800;
        color: ${colors.primary};
        padding-bottom: 8px;
        margin-bottom: 8px;
        border-bottom: 1px solid ${tooltipBorder};
        white-space: normal;
        word-break: break-word;
      ">
        ${titlePrefix}${title}
      </div>

      <div style="
        display: flex;
        justify-content: space-between;
        gap: 18px;
        align-items: center;
      ">
        <span style="
          color: ${tooltipMuted};
          font-size: 12px;
        ">
          ${footerLabel}
        </span>

        <span style="
          color: ${tooltipText};
          font-size: 13px;
          font-weight: 900;
          white-space: nowrap;
        ">
          ${Number(value || 0).toLocaleString("en-US")} gal
        </span>
      </div>
    </div>
  `;
  };
  // =========================
  // AGGREGATE CONFIG
  // =========================
  const aggregateCategoryKey = useMemo(() => {
    return chartData.aggregate.map((item) => item.x).join("|");
  }, [chartData.aggregate]);

  const aggregateXAxisLabelMap = useMemo(() => {
    const labelMap = new Map();

    chartData.aggregate.forEach((item) => {
      labelMap.set(String(item.x), item.xLabel || item.label || item.x);
    });

    return labelMap;
  }, [chartData.aggregate]);

  const displayAggregateSeries = useMemo(() => {
  if (aggType !== "bar") return aggregateSeries;

  return aggregateSeries.map((seriesItem) => ({
    ...seriesItem,
    data: seriesItem.data.map((point) => {
      const actualFlow = Number(point.actualFlow ?? point.y ?? 0);

      return {
        ...point,
        y: actualFlow === 0 ? 0.02 : actualFlow,
        fillColor: actualFlow === 0 ? "rgba(18, 191, 196, 0)" : undefined,
      };
    }),
  }));
}, [aggregateSeries, aggType]);

  const aggregateConfig = useMemo(() => {
    const pointCount = chartData.aggregate.length;
    const isLargeData = pointCount > 80;

    const chartColors = [
      "#12bfc4",
      "#ff6f69",
      "#f7c948",
      "#7c5cff",
      "#2ea8ff",
      "#30d158",
      "#ff9f1c",
      "#c05be3",
      "#00c2ff",
      "#ff5c8a",
    ];

    return {
      ...themedChartBase,

      chart: {
        ...themedChartBase.chart,
        type: aggType,
        height: 320,
        stacked: aggType === "bar",
        toolbar: {
          ...themedChartBase.chart.toolbar,
          show: true,
        },
        zoom: {
          enabled: true,
          type: "x",
          autoScaleYaxis: true,
          allowMouseWheelZoom: isAggregateWheelZoomActive,
        },

        events: {
          mounted: (chartContext) => {
            removeApexDefaultTitle(chartContext);
          },
          updated: (chartContext) => {
            removeApexDefaultTitle(chartContext);
          },
        },
      },

      xaxis: {
        type: "category",
        categories: chartData.aggregate.map((item) => item.x),
        tickPlacement: "on",

        labels: {
          show: true,
          rotate: 0,
          hideOverlappingLabels: true,
          trim: false,
          minHeight: 35,
          style: {
            colors: colors.textSecondary,
            fontSize: "11px",
          },
          formatter: (value) => {
            return aggregateXAxisLabelMap.get(String(value)) || "";
          },
        },

        axisTicks: {
          show: true,
        },

        axisBorder: {
          show: true,
        },

        tooltip: {
          enabled: false,
        },
      },

      yaxis: {
        min: 0,
        max: (max) => {
  return Number(max || 0) <= 0.05 ? 1 : max;
},
        title: {
          text: "Gallons",
          style: {
            color: colors.textSecondary,
            fontSize: "12px",
            fontWeight: 500,
            fontFamily: "inherit",
          },
        },
        labels: {
          formatter: (v) => formatToK(v),
          style: {
            colors: "#8ba1c7",
            fontSize: "11px",
          },
        },
      },

      colors: chartColors,

      dataLabels: {
        enabled: false,
      },

      stroke:
        aggType === "line"
          ? {
              width: 2.5,
              curve: "smooth",
            }
          : {
              width: 0,
            },

      markers:
        aggType === "line"
          ? {
              size: 3,
            }
          : {
              size: 0,
            },

      plotOptions:
        aggType === "bar"
          ? {
              bar: {
                 borderRadius: 2,
          columnWidth: isLargeData ? "70%" : "55%",
          distributed: false,
              },
            }
          : {},

      fill: {
        opacity: 1,
      },

      legend: {
        show: true,

        // Important: Apex hides legend when only one series exists.
        // This will show "District Aggregate Flow" when no filter is selected.
        showForSingleSeries: true,

        position: "bottom",
        horizontalAlign: "center",

        labels: {
          colors: colors.text,
          useSeriesColors: false,
        },

        fontSize: "12px",
        fontWeight: 600,

        markers: {
          width: 12,
          height: 12,
          radius: 12,
        },

        itemMargin: {
          horizontal: 10,
          vertical: 6,
        },
      },

      // ✅ Hide tooltip completely
      tooltip: {
        shared: true,
        intersect: false,
        theme: theme === "white" || theme === "light" ? "light" : "dark",

        custom: ({ series, dataPointIndex, w }) => {
          const isLightTheme = theme === "white" || theme === "light";

          const tooltipBg = isLightTheme ? "#ffffff" : colors.background;
          const tooltipText = colors.text;
          const tooltipMuted = colors.textSecondary;
          const tooltipBorder = colors.border || `${colors.primary}40`;
          const tooltipShadow = isLightTheme
            ? "0 8px 24px rgba(15, 23, 42, 0.12)"
            : "0 8px 24px rgba(0, 0, 0, 0.35)";

          const configSeries = w?.config?.series || [];
          const firstPoint = configSeries?.[0]?.data?.[dataPointIndex];

          const flowTime = firstPoint?.flowTime || "";

          const total = configSeries.reduce((sum, item, index) => {
            const point = item?.data?.[dataPointIndex];

            const value = Number(
              point?.actualFlow ?? point?.y ?? series?.[index]?.[dataPointIndex] ?? 0,
            );

            return sum + value;
          }, 0);

          const rows = configSeries
            .map((item, index) => {
              const point = item?.data?.[dataPointIndex];

              const value = Number(
                point?.actualFlow ?? point?.y ?? series?.[index]?.[dataPointIndex] ?? 0,
              );

              if (Number.isNaN(value)) return "";

              const markerColor = chartColors[index % chartColors.length];

              return `
          <div style="
            display: flex;
            justify-content: space-between;
            gap: 16px;
            margin-top: 8px;
            align-items: center;
          ">
            <div style="
              display: flex;
              align-items: center;
              gap: 8px;
              min-width: 0;
            ">
              <span style="
                width: 10px;
                height: 10px;
                border-radius: 2px;
                background: ${markerColor};
                display: inline-block;
                flex-shrink: 0;
              "></span>

              <span style="
                color: ${tooltipMuted};
                font-size: 12px;
                max-width: 190px;
                white-space: normal;
                word-break: break-word;
                line-height: 1.25;
              ">
                ${item.name}
              </span>
            </div>

            <span style="
              color: ${tooltipText};
              font-size: 12px;
              font-weight: 700;
              white-space: nowrap;
            ">
              ${value.toLocaleString("en-US")} gal
            </span>
          </div>
        `;
            })
            .join("");

          return `
      <div style="
        background: ${tooltipBg};
        border: 1px solid ${tooltipBorder};
        border-radius: 0px;
        padding: 10px 12px;
        min-width: 260px;
        max-width: 360px;
        color: ${tooltipText};
        box-shadow: ${tooltipShadow};
        font-family: inherit;
      ">
        <div style="
          font-size: 13px;
          font-weight: 700;
          color: ${colors.primary};
          margin-bottom: 8px;
          white-space: normal;
          word-break: break-word;
        ">
          ${flowTime}
        </div>

        ${
          rows ||
          `
            <div style="
              color: ${tooltipMuted};
              font-size: 12px;
              font-weight: 600;
            ">
              No flow data available.
            </div>
          `
        }

        <div style="
          display: flex;
          justify-content: space-between;
          gap: 16px;
          margin-top: 10px;
          padding-top: 8px;
          border-top: 1px solid ${tooltipBorder};
        ">
          <span style="
            color: ${tooltipMuted};
            font-size: 12px;
          ">
            Total Flow
          </span>

          <span style="
            color: ${tooltipText};
            font-size: 13px;
            font-weight: 800;
          ">
            ${total.toLocaleString("en-US")} gal
          </span>
        </div>
      </div>
    `;
        },
      },

      grid: {
        borderColor: "#233d5e",
        strokeDashArray: 3,
        padding: {
          left: 10,
          right: 20,
          bottom: 25,
        },
      },
    };
  }, [
    aggType,
    chartData.aggregate,
    chartData.aggregate.length,
    aggregateCategoryKey,
    aggregateXAxisLabelMap,
    flowInterval,
    themedChartBase,
    colors.background,
    colors.border,
    colors.primary,
    colors.text,
    colors.textSecondary,
    theme,
    isAggregateWheelZoomActive,
  ]);
  // tooltip

  const getChartColor = (index) => {
    const chartColors = [
      "#12bfc4",
      "#ff6f69",
      "#f7c948",
      "#7c5cff",
      "#2ea8ff",
      "#30d158",
      "#ff9f1c",
      "#c05be3",
      "#00c2ff",
      "#ff5c8a",
    ];

    return chartColors[index % chartColors.length];
  };

  const getSpecialDayColor = (label, seriesIndex) => {
    if (label === "Weekday" || label === "Weekdays") {
      return "#facc15"; // yellow for weekday
    }

    if (label === "Weekend" || label === "Weekends") {
      return "#fb7185"; // pink/red for weekend
    }

    return getChartColor(seriesIndex); // normal account colors
  };

  const buildStackedTooltip = ({
    series,
    dataPointIndex,
    w,
    titlePrefix = "",
  }) => {
    const configSeries = w?.config?.series || [];
    const xLabels = w?.globals?.labels || w?.config?.xaxis?.categories || [];

    const title = xLabels?.[dataPointIndex] || "";

    const rowsData = configSeries
      .map((item, index) => {
        const point = item?.data?.[dataPointIndex];

        const value = Number(
          point?.y ?? series?.[index]?.[dataPointIndex] ?? 0,
        );

        return {
          name: item?.name || point?.accountName || "Unknown Account",
          value,
          color: point?.fillColor || getChartColor(index),
        };
      })
      .filter((item) => item.value > 0);

    const total = rowsData.reduce((sum, item) => sum + item.value, 0);

    const rows = rowsData
      .map(
        (item) => `
        <div style="
          display:flex;
          justify-content:space-between;
          gap:18px;
          margin-top:8px;
          align-items:center;
        ">
          <div style="
            display:flex;
            align-items:center;
            gap:8px;
            min-width:0;
          ">
            <span style="
              width:10px;
              height:10px;
              border-radius:2px;
              background:${item.color};
              display:inline-block;
              flex-shrink:0;
            "></span>

            <span style="
              color:#dcecff;
              font-size:12px;
              max-width:230px;
              white-space:normal;
              word-break:break-word;
              line-height:1.25;
            ">
              ${item.name}
            </span>
          </div>

          <span style="
            color:#ffffff;
            font-size:12px;
            font-weight:800;
            white-space:nowrap;
          ">
            ${item.value.toLocaleString()} gal
          </span>
        </div>
      `,
      )
      .join("");

    return `
    <div style="
      background:#061b2d;
      border:1px solid #2ea8ff;
      border-radius:10px;
      padding:10px 12px;
      min-width:310px;
      max-width:430px;
      color:#ffffff;
      box-shadow:0 8px 24px rgba(0,0,0,0.38);
      font-family:inherit;
    ">
      <div style="
        font-size:13px;
        font-weight:800;
        color:#ffffff;
        padding-bottom:8px;
        margin-bottom:8px;
        border-bottom:1px solid #1f3d5f;
      ">
        ${titlePrefix}${title}
      </div>

      ${
        rows ||
        `
          <div style="color:#8ba1c7;font-size:12px;">
            No flow data available.
          </div>
        `
      }

      <div style="
        display:flex;
        justify-content:space-between;
        gap:18px;
        margin-top:10px;
        padding-top:8px;
        border-top:1px solid #23446f;
      ">
        <span style="color:#8ba1c7;font-size:12px;">Total Flow</span>
        <span style="color:#ffffff;font-size:13px;font-weight:900;">
          ${total.toLocaleString()} gal
        </span>
      </div>
    </div>
  `;
  };

  const buildTotalTooltip = ({
    dataPointIndex,
    w,
    titlePrefix = "",
    detailsMap = {},
    footerLabel = "Average Flow",
  }) => {
    const xLabels = w?.globals?.labels || w?.config?.xaxis?.categories || [];
    const title = xLabels?.[dataPointIndex] || "";

    const rowsData = detailsMap?.[title] || [];

    const chartPoint = w?.config?.series?.[0]?.data?.[dataPointIndex];

    const footerValue = Number(
      chartPoint?.y ??
        w?.globals?.series?.[0]?.[dataPointIndex] ??
        rowsData.reduce((sum, item) => sum + Number(item.value || 0), 0),
    );

    const rows = rowsData
      .map(
        (item) => `
        <div style="
          display:flex;
          justify-content:space-between;
          gap:18px;
          margin-top:8px;
          align-items:center;
        ">
          <div style="
            display:flex;
            align-items:center;
            gap:8px;
            min-width:0;
          ">
            <span style="
              color:#dcecff;
              font-size:12px;
              max-width:240px;
              white-space:normal;
              word-break:break-word;
              line-height:1.25;
            ">
              ${item.name}
            </span>
          </div>

          <span style="
            color:#ffffff;
            font-size:12px;
            font-weight:800;
            white-space:nowrap;
          ">
            ${Number(item.value || 0).toLocaleString()} gal
          </span>
        </div>
      `,
      )
      .join("");

    return `
    <div style="
      background:#061b2d;
      border:1px solid #2ea8ff;
      border-radius:10px;
      padding:10px 12px;
      min-width:320px;
      max-width:450px;
      color:#ffffff;
      box-shadow:0 8px 24px rgba(0,0,0,0.38);
      font-family:inherit;
    ">
      <div style="
        font-size:13px;
        font-weight:800;
        color:#ffffff;
        padding-bottom:8px;
        margin-bottom:8px;
        border-bottom:1px solid #1f3d5f;
      ">
        ${titlePrefix}${title}
      </div>

      ${
        rows ||
        `
          <div style="color:#8ba1c7;font-size:12px;">
            No average flow data available.
          </div>
        `
      }

      <div style="
        display:flex;
        justify-content:space-between;
        gap:18px;
        margin-top:10px;
        padding-top:8px;
        border-top:1px solid #23446f;
      ">
        <span style="color:#8ba1c7;font-size:12px;">${footerLabel}</span>
        <span style="color:#ffffff;font-size:13px;font-weight:900;">
          ${footerValue.toLocaleString()} gal
        </span>
      </div>
    </div>
  `;
  };
  // =========================
  // DAILY CONFIG
  // =========================
const displayDailyAverageSeries = useMemo(() => {
  if (dailyType !== "bar") return dailyAverageSeries;

  return dailyAverageSeries.map((seriesItem) => ({
    ...seriesItem,
    data: seriesItem.data.map((point) => {
      const actualFlow = Number(point.actualFlow ?? point.y ?? 0);

      return {
        ...point,
        y: actualFlow === 0 ? 0.02 : actualFlow,
        actualFlow,
        fillColor:
          actualFlow === 0
            ? "rgba(18, 191, 196, 0)"
            : point.fillColor,
      };
    }),
  }));
}, [dailyAverageSeries, dailyType]);


  const dailyChartSeries = useMemo(() => {
    if (dailyType !== "line") {
       return displayDailyAverageSeries;
    }

    const points = chartData.dailyAvg || [];

    const normalDayData = points.map((point) =>
      isWeekGroupLabel(point.x) ? { ...point, y: null } : point,
    );

    const weekdayData = points.map((point, index) => {
      const prevPoint = points[index - 1];

      if (point.x === "Weekday") return point;

      // This keeps the line connected from Sunday to Weekday
      if (prevPoint?.x === "Weekday") return point;

      if (point.x === "Sunday") return point;

      return { ...point, y: null };
    });

    const weekendData = points.map((point, index) => {
      const prevPoint = points[index - 1];

      if (point.x === "Weekend") return point;

      // This keeps the line connected from Weekday to Weekend
      if (point.x === "Weekday") return point;

      if (prevPoint?.x === "Weekend") return point;

      return { ...point, y: null };
    });

    return [
      {
        name: "Daily Avg",
        data: normalDayData,
      },
      {
        name: "Weekday Avg",
        data: weekdayData,
      },
      {
        name: "Weekend Avg",
        data: weekendData,
      },
    ];
  }, [dailyType, displayDailyAverageSeries, chartData.dailyAvg]);

  const dailyConfig = useMemo(() => {
    return {
      ...themedChartBase,

      chart: {
        ...themedChartBase.chart,
        type: dailyType,
        height: 420,
        stacked: false,

        events: {
          mounted: (chartContext) => {
            removeApexDefaultTitle(chartContext);
          },
          updated: (chartContext) => {
            removeApexDefaultTitle(chartContext);
          },
        },
      },

      series: [
        {
          name: "Daily Avg",
          data: dailyAvg.map((p) => p.y),
        },
      ],

      xaxis: {
        type: "category",
        categories: chartData.dailyAvg.map((item) => item.x),
        tooltip: {
          enabled: false,
        },
        labels: {
          style: {
            colors: chartData.dailyAvg.map((item) =>
              isWeekGroupLabel(item.x)
                ? AVG_CHART_COLORS.groupLabel
                : colors.textSecondary,
            ),
            fontSize: "12px",
            fontWeight: 700,
          },
        },
      },

      yaxis: {
          min: 0,
          max: (max) => {
            return Number(max || 0) <= 0.05 ? 1 : max;
          },
        title: {
          text: "Gallons",
          style: {
            color: colors.textSecondary,
            fontSize: "12px",
            fontWeight: 500,
            fontFamily: "inherit",
          },
        },
        labels: {
          formatter: (v) => formatToK(v),
          style: {
            colors: colors.textSecondary,
          },
        },
      },

      colors: ["#12bfc4"],

      dataLabels: {
        enabled: false,
      },

      stroke:
        dailyType === "line"
          ? {
              width: [4, 4, 4],
              curve: "smooth",
            }
          : {
              width: 0,
            },

      markers:
        dailyType === "line"
          ? {
              size: 5,
              strokeWidth: 2,
              strokeColors: "#ffffff",
            }
          : {
              size: 0,
            },

      plotOptions:
        dailyType === "bar"
          ? {
              bar: {
                distributed: true,
                borderRadius: 6,
                columnWidth: "70%",
              },
            }
          : {},

    colors:
  dailyType === "bar"
    ? displayDailyAverageSeries?.[0]?.data?.map((item) => item.fillColor)
    : [
        AVG_CHART_COLORS.dayLine,
        AVG_CHART_COLORS.weekdayLine,
        AVG_CHART_COLORS.weekendLine,
      ],

      tooltip: {
        shared: true,
        intersect: false,
        followCursor: true,
        theme: theme === "white" || theme === "light" ? "light" : "dark",
        custom: (opts) =>
          buildAverageOnlyTooltip({
            ...opts,
            titlePrefix: "",
            footerLabel: "Average Flow",
            sourcePoints: chartData.dailyAvg,
          }),
      },

      legend: {
        show: true,
        position: "bottom",
        labels: {
          colors: colors.text,
        },
      },
    };
  }, [
    dailyType,
    themedChartBase,
    colors.background,
    colors.border,
    colors.primary,
    colors.text,
    colors.textSecondary,
    theme,
    dailyAverageSeries.length,
    dailyAverageDetails,
    dailyChartSeries.length,
    chartData.dailyAvg,
  ]);

  // =========================
  // HOURLY CONFIG
  // =========================

  const hourlyChartColor =
    hourlyDay === "Weekdays" || hourlyDay === "Weekends"
      ? "#b8e986"
      : "#12bfc4";

const displayHourlyAverageSeries = useMemo(() => {
  if (hourlyType !== "bar") return hourlyAverageSeries;

  return hourlyAverageSeries.map((seriesItem) => ({
    ...seriesItem,
    data: seriesItem.data.map((point) => {
      const actualFlow = Number(point.actualFlow ?? point.y ?? 0);

      return {
        ...point,
        y: actualFlow === 0 ? 0.02 : actualFlow,
        actualFlow,
        fillColor:
          actualFlow === 0
            ? "rgba(18, 191, 196, 0)"
            : point.fillColor || hourlyChartColor,
      };
    }),
  }));
}, [hourlyAverageSeries, hourlyType, hourlyChartColor]);

  const hourlyConfig = useMemo(() => {
    return {
      ...themedChartBase,

      chart: {
        ...themedChartBase.chart,
        type: hourlyType,
        height: 420,
        stacked: false,
        zoom: {
          enabled: true,
          type: "x",
          autoScaleYaxis: false,
          allowMouseWheelZoom: isHourlyWheelZoomActive,
        },

        events: {
          mounted: (chartContext) => {
            removeApexDefaultTitle(chartContext);
          },
          updated: (chartContext) => {
            removeApexDefaultTitle(chartContext);
          },
        },
      },

      series: [
        {
          name: "Total Demand Hourly Avg",
          data: hourlyAvg.map((p) => p.y),
        },
      ],

      xaxis: {
        categories: hourlyAvg.map((p) => p.x),
        tooltip: {
          enabled: false,
        },

        labels: {
          style: {
            colors: colors.textSecondary,
            fontSize: "11px",
          },
        },
      },

      yaxis: {
        min: 0,
        forceNiceScale: true,

       max: (max) => {
  return Number(max || 0) <= 0.05 ? 1 : max;
},

        title: {
          text: "Gallons",
          style: {
            color: colors.textSecondary,
            fontSize: "12px",
            fontWeight: 500,
            fontFamily: "inherit",
          },
        },

        labels: {
          formatter: (v) => formatToK(v),
          style: {
            colors: colors.textSecondary,
          },
        },
      },

      legend: {
        show: true,

        labels: {
          colors: colors.textSecondary,
        },
      },

      dataLabels: {
        enabled: false,
      },

      colors: [hourlyChartColor],

      stroke:
        hourlyType === "line"
          ? {
              width: 3,
              curve: "monotoneCubic",
            }
          : {
              width: 0,
            },

      markers:
        hourlyType === "line"
          ? {
              size: 6,
              colors: [hourlyChartColor],
              strokeColors: [hourlyChartColor],
              strokeWidth: 2,
              hover: {
                size: 8,
              },
            }
          : {
              size: 0,
            },

      plotOptions:
        hourlyType === "bar"
          ? {
              bar: {
                borderRadius: 4,
                columnWidth: "70%",
              },
            }
          : {},

      tooltip: {
        shared: true,
        intersect: false,
        followCursor: true,
        theme: theme === "white" || theme === "light" ? "light" : "dark",
        custom: (opts) =>
          buildAverageOnlyTooltip({
            ...opts,
            titlePrefix: `${hourlyDay} - `,
            footerLabel: "Average Flow",
          }),
      },
    };
  }, [
    hourlyType,
    hourlyDay,
    hourlyChartColor,
    themedChartBase,
    colors.background,
    colors.border,
    colors.primary,
    colors.text,
    colors.textSecondary,
    theme,
    hourlyAverageSeries.length,
    hourlyAverageDetails,
    isHourlyWheelZoomActive,
  ]);

  // =========================
  // CATEGORY CONFIG
  // =========================
  const getBreakdownColor = (item, index) => {
    const name = String(
      item?.name || item?.categoryName || item?.code_name || "",
    )
      .toLowerCase()
      .trim();

    // Only Potable Domestic should be blue
    if (name === "potable domestic") {
      return "#0284c7";
    }

    // Non-Potable categories should be purple shade
    if (name.includes("non-potable") || name.includes("non potable")) {
      return "#9333ea";
    }

    // Other categories should NOT use blue
    const fallbackColors = [
      "#f59e0b", // orange
      "#22c55e", // green
      "#ef4444", // red
      "#eab308", // yellow
      "#14b8a6", // teal
      "#a855f7", // violet
      "#84cc16", // lime
      "#f97316", // orange dark
    ];

    return fallbackColors[index % fallbackColors.length];
  };

  const formatApiPercentage = (value) => {
    if (value === null || value === undefined || value === "") return "0";

    const numberValue = Number(value);

    if (Number.isNaN(numberValue)) return "0";

    return String(numberValue);
  };

  const isLightTheme = theme === "white" || theme === "light";

  const donutTooltipBg = isLightTheme ? "#ffffff" : colors.background;
  const donutTooltipText = colors.text;
  const donutTooltipMuted = colors.textSecondary;
  const donutTooltipBorder = colors.border || `${colors.primary}40`;
  const donutTooltipShadow = isLightTheme
    ? "0 8px 24px rgba(15, 23, 42, 0.12)"
    : "0 8px 24px rgba(0, 0, 0, 0.35)";

  const categoryConfig = {
    ...themedChartBase,

    chart: {
      ...themedChartBase.chart,
      type: "donut",
      height: 320,
      toolbar: {
        show: false,
      },

      events: {
        mounted: (chartContext) => {
          removeApexDefaultTitle(chartContext);

          setTimeout(() => removeApexDefaultTitle(chartContext), 100);
          setTimeout(() => removeApexDefaultTitle(chartContext), 300);
        },

        updated: (chartContext) => {
          removeApexDefaultTitle(chartContext);

          setTimeout(() => removeApexDefaultTitle(chartContext), 100);
          setTimeout(() => removeApexDefaultTitle(chartContext), 300);
        },

        dataPointMouseEnter: (event, chartContext) => {
          removeApexDefaultTitle(chartContext);

          setTimeout(() => removeApexDefaultTitle(chartContext), 0);
        },

        mouseMove: (event, chartContext) => {
          removeApexDefaultTitle(chartContext);
        },

        legendClick: (chartContext, seriesIndex) => {
          setTimeout(() => {
            removeApexDefaultTitle(chartContext);

            const chartEl = chartContext?.el;

            if (!chartEl) return;

            const pieSlices = chartEl.querySelectorAll(
              ".apexcharts-pie-series path",
            );

            const selectedSlice = pieSlices?.[seriesIndex];

            if (!selectedSlice) return;

            const rect = selectedSlice.getBoundingClientRect();

            const mouseMoveEvent = new MouseEvent("mousemove", {
              bubbles: true,
              cancelable: true,
              clientX: rect.left + rect.width / 2,
              clientY: rect.top + rect.height / 2,
            });

            const mouseEnterEvent = new MouseEvent("mouseenter", {
              bubbles: true,
              cancelable: true,
              clientX: rect.left + rect.width / 2,
              clientY: rect.top + rect.height / 2,
            });

            selectedSlice.dispatchEvent(mouseEnterEvent);
            selectedSlice.dispatchEvent(mouseMoveEvent);

            removeApexDefaultTitle(chartContext);
          }, 80);

          return false;
        },
      },
    },

    series: categoryBreakdown.map((c) => c.value),
    labels: categoryBreakdown.map((c) => c.name),

    colors: categoryBreakdown.map((item, index) =>
      getBreakdownColor(item, index),
    ),

    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",

      labels: {
        colors: colors.text,
        useSeriesColors: false,
      },

      fontSize: "12px",
      fontWeight: 600,

      markers: {
        width: 12,
        height: 12,
        radius: 12,
      },

      itemMargin: {
        horizontal: 8,
        vertical: 6,
      },

      onItemClick: {
        toggleDataSeries: false,
      },
    },

    dataLabels: {
      enabled: true,
      formatter: (val, opts) => {
        const item = categoryBreakdown?.[opts.seriesIndex];

        return `${formatApiPercentage(item?.sharePct)}%`;
      },

      style: {
        fontSize: "12px",
        fontWeight: 700,
        colors: ["#000000"],
      },

      dropShadow: {
        enabled: false,
      },
    },

    stroke: {
      width: 2,
      colors: [colors.cardBg || colors.background],
    },

    plotOptions: {
      pie: {
        expandOnClick: false,

        donut: {
          size: "62%",

          labels: {
            show: true,

            name: {
              show: true,
              color: colors.textSecondary,
              fontSize: "16px",
              fontWeight: 400,
              offsetY: -8,
              formatter: () => "Total gal",
            },

            value: {
              show: true,
              color: colors.text,
              fontSize: "24px",
              fontWeight: 500,
              offsetY: 8,
              formatter: () => Number(totalFlowGallons || 0).toLocaleString(),
            },

            total: {
              show: true,
              showAlways: true,
              label: "Total gal",
              color: colors.textSecondary,
              fontSize: "16px",
              fontWeight: 400,
              formatter: () => Number(totalFlowGallons || 0).toLocaleString(),
            },
          },
        },
      },
    },

    tooltip: {
      enabled: true,
      theme: isLightTheme ? "light" : "dark",

      custom: ({ seriesIndex }) => {
        const item = categoryBreakdown[seriesIndex];

        if (!item) return "";

        const flowValue = Number(item.value || 0).toLocaleString("en-US");
        const sharePct = formatApiPercentage(item.sharePct);
        const markerColor = getBreakdownColor(item, seriesIndex);

        return `
      <div style="
        background: ${donutTooltipBg};
        border: 1px solid ${donutTooltipBorder};
        border-radius: 0px;
        padding: 10px 12px;
        min-width: 220px;
        max-width: 300px;
        color: ${donutTooltipText};
        box-shadow: ${donutTooltipShadow};
        font-family: inherit;
      ">
        <div style="
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 700;
          color: ${colors.primary};
          margin-bottom: 8px;
          white-space: normal;
          word-break: break-word;
        ">
          <span style="
            width: 10px;
            height: 10px;
            border-radius: 999px;
            background: ${markerColor};
            display: inline-block;
            flex-shrink: 0;
          "></span>

          <span>${item.name || "Unknown"}</span>
        </div>

        <div style="
          display: flex;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 6px;
        ">
          <span style="
            color: ${donutTooltipMuted};
            font-size: 12px;
          ">
            Flow
          </span>

          <span style="
            color: ${donutTooltipText};
            font-size: 12px;
            font-weight: 700;
          ">
            ${flowValue} gal
          </span>
        </div>

        <div style="
          display: flex;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 6px;
        ">
          <span style="
            color: ${donutTooltipMuted};
            font-size: 12px;
          ">
            Share
          </span>

          <span style="
            color: ${donutTooltipText};
            font-size: 12px;
            font-weight: 700;
          ">
            ${sharePct}%
          </span>
        </div>

        ${
          item.meterName
            ? `
              <div style="
                margin-top: 8px;
                padding-top: 8px;
                border-top: 1px solid ${donutTooltipBorder};
              ">
                <div style="
                  color: ${donutTooltipMuted};
                  font-size: 11px;
                  margin-bottom: 2px;
                ">
                  Meter
                </div>

                <div style="
                  color: ${donutTooltipText};
                  font-size: 12px;
                  font-weight: 700;
                  word-break: break-word;
                ">
                  ${item.meterName}
                </div>
              </div>
            `
            : ""
        }

        ${
          item.accountName
            ? `
              <div style="margin-top: 8px;">
                <div style="
                  color: ${donutTooltipMuted};
                  font-size: 11px;
                  margin-bottom: 2px;
                ">
                  Account
                </div>

                <div style="
                  color: ${donutTooltipText};
                  font-size: 12px;
                  font-weight: 700;
                  word-break: break-word;
                ">
                  ${item.accountName}
                </div>
              </div>
            `
            : ""
        }

        ${
          item.categoryName
            ? `
              <div style="margin-top: 8px;">
                <div style="
                  color: ${donutTooltipMuted};
                  font-size: 11px;
                  margin-bottom: 2px;
                ">
                  Category
                </div>

                <div style="
                  color: ${donutTooltipText};
                  font-size: 12px;
                  font-weight: 700;
                  word-break: break-word;
                ">
                  ${item.categoryName}
                </div>
              </div>
            `
            : ""
        }
      </div>
    `;
      },
    },
  };

  // =========================
  // ACCOUNT CONFIG
  // =========================

  // const accountConfig = useMemo(() => {
  //   return {
  //     ...themedChartBase,

  //     chart: {
  //       ...themedChartBase.chart,
  //       type: acctType,
  //       height: 300,
  //     },

  //     xaxis: {
  //       categories: liveTopAccounts.map((a) => a.name),

  //       labels: {
  //         style: {
  //           colors: colors.textSecondary,
  //         },
  //       },
  //     },

  //     yaxis: {
  //       labels: {
  //         formatter: (value) => Number(value || 0).toLocaleString(),
  //         style: {
  //           colors: colors.textSecondary,
  //         },
  //       },
  //     },

  //     colors: ["#2ea8ff"],

  //     dataLabels: {
  //       enabled: true,
  //       formatter: (value) => Number(value || 0).toLocaleString(),
  //       offsetY: acctType === "line" ? -10 : 0,
  //       background: {
  //         enabled: false,
  //       },
  //       style: {
  //         colors: ["#ffffff"],
  //         fontSize: "12px",
  //         fontWeight: 700,
  //       },
  //     },

  //     plotOptions:
  //       acctType === "bar"
  //         ? {
  //             bar: {
  //               horizontal: true,
  //               borderRadius: 4,
  //               barHeight: "55%",
  //             },
  //           }
  //         : {},

  //     stroke:
  //       acctType === "line"
  //         ? {
  //             width: 3,
  //             curve: "smooth",
  //           }
  //         : {
  //             width: 0,
  //           },

  //     markers:
  //       acctType === "line"
  //         ? {
  //             size: 5,
  //             strokeWidth: 2,
  //             strokeColors: "#ffffff",
  //             hover: {
  //               size: 7,
  //             },
  //           }
  //         : {
  //             size: 0,
  //           },

  //     tooltip: {
  //       theme: "dark",
  //       y: {
  //         formatter: (value) => `${Number(value || 0).toLocaleString()} gal`,
  //       },
  //     },
  //   };
  // }, [acctType, liveTopAccounts]);

  const shortenLabel = (label, maxLength = 18) => {
    const text = String(label || "");

    if (text.length <= maxLength) return text;

    return `${text.slice(0, maxLength)}...`;
  };

  const accountConfig = useMemo(() => {
    const accountNames = liveTopAccounts.map((a) => a.name);
    const accountValues = liveTopAccounts.map((a) => Number(a.value || 0));

    return {
      ...themedChartBase,

      chart: {
        ...themedChartBase.chart,
        type: acctType,
        height: 300,
        toolbar: {
          ...themedChartBase.chart.toolbar,
          show: true,
        },

        events: {
          mounted: (chartContext) => {
            removeApexDefaultTitle(chartContext);
          },
          updated: (chartContext) => {
            removeApexDefaultTitle(chartContext);
          },
        },
      },

      xaxis: {
        categories: accountNames,
        tooltip: {
          enabled: false,
        },
        labels: {
          rotate: acctType === "line" ? -45 : 0,
          rotateAlways: acctType === "line",
          trim: true,
          hideOverlappingLabels: true,

          formatter: (value) => {
            if (acctType === "bar") {
              // return Number(value || 0).toLocaleString();
              return formatToK(value);
            }

            return shortenLabel(value, 16);
          },

          style: {
            colors: colors.textSecondary,
            fontSize: "11px",
          },
        },

        // tooltip: {
        //   enabled: false,
        // },
      },
      yaxis: {
        labels: {
          formatter: (value) => {
            if (acctType === "bar") {
              return shortenLabel(value, 18);
            }

            // return Number(value || 0).toLocaleString();
            return formatToK(value);
          },

          style: {
            colors: colors.textSecondary,
            fontSize: "11px",
          },
        },
      },

      colors: ["#2ea8ff"],

      dataLabels: {
        enabled: true,

        // formatter: (value) => Number(value || 0).toLocaleString(),
        formatter: (value) => formatToK(value),

        offsetY: acctType === "line" ? -10 : 0,

        background: {
          enabled: false,
        },

        style: {
          colors: ["#ffffff"],
          fontSize: "12px",
          fontWeight: 700,
        },
      },

      plotOptions:
        acctType === "bar"
          ? {
              bar: {
                horizontal: true,
                borderRadius: 4,
                barHeight: "55%",
                dataLabels: {
                  position: "center",
                },
              },
            }
          : {},

      stroke:
        acctType === "line"
          ? {
              width: 3,
              curve: "smooth",
            }
          : {
              width: 0,
            },

      markers:
        acctType === "line"
          ? {
              size: 5,
              strokeWidth: 2,
              strokeColors: "#ffffff",
              hover: {
                size: 7,
              },
            }
          : {
              size: 0,
            },

      tooltip: {
        enabled: true,
        theme: theme === "white" || theme === "light" ? "light" : "dark",

        custom: ({ series, seriesIndex, dataPointIndex }) => {
          const isLightTheme = theme === "white" || theme === "light";

          const accountName = accountNames[dataPointIndex] || "Unknown Account";
          const value = Number(
            series?.[seriesIndex]?.[dataPointIndex] || 0,
          ).toLocaleString("en-US");

          const tooltipBg = isLightTheme ? "#ffffff" : colors.background;
          const tooltipText = colors.text;
          const tooltipMuted = colors.textSecondary;
          const tooltipBorder = colors.border || `${colors.primary}40`;
          const tooltipShadow = isLightTheme
            ? "0 8px 24px rgba(15, 23, 42, 0.12)"
            : "0 8px 24px rgba(0, 0, 0, 0.35)";

          return `
      <div style="
        background: ${tooltipBg};
        border: 1px solid ${tooltipBorder};
        border-radius: 0px;
        padding: 10px 12px;
        min-width: 240px;
        max-width: 340px;
        color: ${tooltipText};
        box-shadow: ${tooltipShadow};
        font-family: inherit;
      ">
        <div style="
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 700;
          color: ${colors.primary};
          margin-bottom: 10px;
          white-space: normal;
          word-break: break-word;
          line-height: 1.3;
        ">
          <span style="
            width: 10px;
            height: 10px;
            border-radius: 999px;
            background: #2ea8ff;
            display: inline-block;
            flex-shrink: 0;
          "></span>

          <span>${accountName}</span>
        </div>

        <div style="
          display: flex;
          justify-content: space-between;
          gap: 14px;
          align-items: center;
        ">
          <span style="
            color: ${tooltipMuted};
            font-size: 12px;
            font-weight: 500;
          ">
            Total Flow
          </span>

          <span style="
            color: ${tooltipText};
            font-size: 12px;
            font-weight: 800;
            white-space: nowrap;
          ">
            ${value} gal
          </span>
        </div>
      </div>
    `;
        },
      },

      grid: {
        borderColor: colors.border,
        strokeDashArray: 3,
        padding: {
          left: acctType === "bar" ? 10 : 20,
          right: 20,
          bottom: acctType === "line" ? 35 : 10,
        },
      },
    };
  }, [
    acctType,
    liveTopAccounts,
    themedChartBase,
    colors.background,
    colors.border,
    colors.primary,
    colors.text,
    colors.textSecondary,
    theme,
  ]);

  // WaterCombined.jsx
  const parseFlowTime = (flowTime) => {
    if (!flowTime) return null;

    // API format: "Tuesday, Apr 14, 2026 12:00 AM"
    const cleaned = String(flowTime).replace(/^[A-Za-z]+,\s*/, "");
    const date = new Date(cleaned);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date.getTime();
  };

  useEffect(() => {
    if (!districtId) return;

    // First render
    if (previousDistrictRef.current === null) {
      previousDistrictRef.current = districtId;
      return;
    }

    // Same district
    if (previousDistrictRef.current === districtId) {
      return;
    }

    previousDistrictRef.current = districtId;

    // Clear selected filters
    setFilters((prev) => ({
      ...prev,
      account: [],
      meter: [],
      category: [],
    }));

    // Clear old UI data
    setOptions({
      accounts: [],
      meters: [],
      categories: [],
    });

    setMeterRankings([]);

    setChartData({
      aggregate: [],
      dailyAvg: [],
      hourlyAvg: [],
      weeklyTrend: [],
    });
    setDailyAverageSeries([]);
    setHourlyAverageSeries([]);
    setHourlyAnalyticsRoots([]);
    setDailyAverageDetails({});
    setHourlyAverageDetails({});

    trendRequestKeyRef.current = "";
    trendAnalyticsRequestKeyRef.current = "";

    setBreakdownData({
      topFlowTitle: "Top Accounts — Total Flow In Range",
      categoryBreakdown: [],
      meterBreakdown: [],
      accountBreakdown: [],
      liveTopAccounts: [],
      totalFlowGallons: 0,
    });

    // Very important: clear request guards when district changes
    allDropdownOptionsRef.current = {
      accounts: [],
      meters: [],
      categories: [],
    };
    optionsRequestKeyRef.current = "";
    kpiRequestKeyRef.current = "";
    rankingRequestKeyRef.current = "";
    trendRequestKeyRef.current = "";
    breakdownRequestKeyRef.current = "";
    trendAnalyticsRequestKeyRef.current = "";
    setMeterAll(true);
  }, [districtId]);

  // Inside WaterCombined.jsx for Dropdown Options

  const mapAccounts = (items = []) =>
    items
      .filter((acc) => acc?.account_name)
      .map((acc) => ({
        id: acc.account_name,
        name: acc.account_name,
      }));

  const mapMeters = (items = []) =>
    items
      .filter((m) => m?.meter_id)
      .map((m) => ({
        id: m.meter_id,
        name: m.meter_id,
      }));

  const mapCategories = (items = []) =>
    items
      .filter((c) => c?.code)
      .map((c) => ({
        code: c.code,
        name: c.code_name || c.code,
      }));

  const uniqueByKey = (items = [], key) => {
    const map = new Map();

    items.forEach((item) => {
      const value = item?.[key];

      if (value !== undefined && value !== null && value !== "") {
        map.set(value, item);
      }
    });

    return Array.from(map.values());
  };

  const getDropdownApiData = (response) => {
    return response?.data?.data || response?.data || response || {};
  };

  // dropdown options
  const normalizeDropdownFilters = (filters = {}) => ({
    account: filters.account || [],
    meter: filters.meter || [],
    category: filters.category || [],
  });

  const buildSelectedAccountOptions = (accounts = []) =>
    accounts.map((name) => ({
      id: name,
      name,
    }));

  const buildSelectedMeterOptions = (meters = []) =>
    meters.map((meterId) => ({
      id: meterId,
      name: meterId,
    }));

  const buildSelectedCategoryOptions = (categories = []) =>
    categories.map((code) => ({
      code,
      name: code,
    }));

  // const fetchDropdownOptions = async (payloadFilters, changedBy = "") => {
  //   const selectedFilters = normalizeDropdownFilters(payloadFilters);

  //   const response = await getAccountMeterCategoryList({
  //     accounts: selectedFilters.account,
  //     meter_ids: selectedFilters.meter,
  //     codes: selectedFilters.category,
  //   });

  //   const apiData = getDropdownApiData(response);

  //   const accountOptions = mapAccounts(apiData.accounts || []);
  //   const meterOptions = mapMeters(apiData.meters || []);
  //   const categoryOptions = mapCategories(apiData.codes || []);

  //   let finalFilters = selectedFilters;

  //   // IMPORTANT:
  //   // When meter is selected, auto-check corresponding accounts and categories
  //   if (changedBy === "meter" && selectedFilters.meter.length > 0) {
  //     finalFilters = {
  //       ...selectedFilters,
  //       account: accountOptions.map((item) => item.id),
  //       category: categoryOptions.map((item) => item.code),
  //     };
  //   }

  //   setOptions({
  //     accounts: uniqueByKey(
  //       [
  //         ...buildSelectedAccountOptions(finalFilters.account),
  //         ...accountOptions,
  //       ],
  //       "id",
  //     ),
  //     meters: uniqueByKey(
  //       [...buildSelectedMeterOptions(finalFilters.meter), ...meterOptions],
  //       "id",
  //     ),
  //     categories: uniqueByKey(
  //       [
  //         ...buildSelectedCategoryOptions(finalFilters.category),
  //         ...categoryOptions,
  //       ],
  //       "code",
  //     ),
  //   });

  //   setOptionFilters(finalFilters);

  //   return finalFilters;
  // };

  // Initial dropdown load only

  const getIds = (items = [], key) =>
    items
      .map((item) => item?.[key])
      .filter((value) => value !== undefined && value !== null && value !== "");

  const fetchDropdownOptions = async (payloadFilters, changedBy = "") => {
    const selectedFilters = normalizeDropdownFilters(payloadFilters);

    const requestKey = JSON.stringify({
      districtId,
      changedBy,
      account: selectedFilters.account,
      meter: selectedFilters.meter,
      category: selectedFilters.category,
      meter_status: draftMeterStatus,
    });

    optionsRequestKeyRef.current = requestKey;

    const response = await getAccountMeterCategoryList({
      accounts: selectedFilters.account,
      meter_ids: selectedFilters.meter,
      codes: selectedFilters.category,
      meter_status: draftMeterStatus,
    });

    // Ignore old API response
    if (optionsRequestKeyRef.current !== requestKey) {
      return selectedFilters;
    }

    const apiData = getDropdownApiData(response);

    const accountOptions = mapAccounts(apiData.accounts || []);
    const meterOptions = mapMeters(apiData.meters || []);
    const categoryOptions = mapCategories(apiData.codes || []);

    let finalFilters = {
      account: selectedFilters.account,
      meter: selectedFilters.meter,
      category: selectedFilters.category,
    };

    /**
     * Important:
     * When meter is selected, API returns related account and category.
     * So auto-check those related account/category values.
     */
    if (changedBy === "meter" && selectedFilters.meter.length > 0) {
      finalFilters = {
        ...finalFilters,
        account: getIds(accountOptions, "id"),
        category: getIds(categoryOptions, "code"),
      };
    }

    /**
     * When account changes, remove old meter/category selected values.
     */
    if (changedBy === "account") {
      finalFilters = {
        account: selectedFilters.account,
        meter: [],
        category: [],
      };
    }

    /**
     * When category changes, keep account/meter as they are only if you want.
     * If category should also clear account/meter, uncomment below.
     */
    if (changedBy === "category") {
      finalFilters = {
        ...finalFilters,
        category: selectedFilters.category,
      };
    }

    const selectedAccountOptions = buildSelectedAccountOptions(
      finalFilters.account,
    );
    const selectedMeterOptions = buildSelectedMeterOptions(finalFilters.meter);
    const selectedCategoryOptions = buildSelectedCategoryOptions(
      finalFilters.category,
    );

    setOptions({
      accounts: uniqueByKey(
        [...selectedAccountOptions, ...accountOptions],
        "id",
      ),
      meters: uniqueByKey([...selectedMeterOptions, ...meterOptions], "id"),
      categories: uniqueByKey(
        [...selectedCategoryOptions, ...categoryOptions],
        "code",
      ),
    });

    setOptionFilters(finalFilters);

    return finalFilters;
  };

  const fetchDigitalTwinMeterRelatedOptions = async (meterId) => {
    const response = await getAccountMeterCategoryList({
      accounts: [],
      meter_ids: [meterId],
      codes: [],

      // Use applied status only.
      // Do not use draftMeterStatus here.
      meter_status: appliedMeterStatus,
    });

    const apiData = getDropdownApiData(response);

    const accountOptions = mapAccounts(apiData.accounts || []);
    const meterOptions = mapMeters(apiData.meters || []);
    const categoryOptions = mapCategories(apiData.codes || []);

    const finalFilters = {
      account: getIds(accountOptions, "id"),
      meter: [meterId],
      category: getIds(categoryOptions, "code"),
    };

    setOptions({
      accounts: uniqueByKey(
        [
          ...buildSelectedAccountOptions(finalFilters.account),
          ...accountOptions,
        ],
        "id",
      ),
      meters: uniqueByKey(
        [...buildSelectedMeterOptions(finalFilters.meter), ...meterOptions],
        "id",
      ),
      categories: uniqueByKey(
        [
          ...buildSelectedCategoryOptions(finalFilters.category),
          ...categoryOptions,
        ],
        "code",
      ),
    });

    setOptionFilters(finalFilters);

    return finalFilters;
  };

  //
  useEffect(() => {
    if (!districtId || !meterIdFromDigitalTwin) return;

    let cancelled = false;

    const applyDigitalTwinMeterFilters = async () => {
      try {
        setLoadingOptions(true);

        const baseFilters = {
          ...DEFAULT_FILTERS,
          mode: "Custom",
          chip: "Custom",
          start: startDateFromDigitalTwin || DEFAULT_FILTERS.start,
          end: endDateFromDigitalTwin || DEFAULT_FILTERS.end,
          interval: DEFAULT_FILTERS.interval || "Day",
          meter: [meterIdFromDigitalTwin],
          account: [],
          category: [],
        };

        /**
         * This API call will return the corresponding account/category
         * because changedBy = "meter".
         */
        const relatedFilters = await fetchDigitalTwinMeterRelatedOptions(
          meterIdFromDigitalTwin,
        );

        if (cancelled) return;

        const finalFilters = {
          ...baseFilters,
          account: relatedFilters?.account || [],
          meter: relatedFilters?.meter?.length
            ? relatedFilters.meter
            : [meterIdFromDigitalTwin],
          category: relatedFilters?.category || [],
        };

        setFilters(finalFilters);

        setOptionFilters({
          account: finalFilters.account,
          meter: finalFilters.meter,
          category: finalFilters.category,
        });

        setFlowInterval(finalFilters.interval || "Day");

        // Clear request guards so all APIs call with final meter + account + category
        kpiRequestKeyRef.current = "";
        rankingRequestKeyRef.current = "";
        trendRequestKeyRef.current = "";
        breakdownRequestKeyRef.current = "";
        trendAnalyticsRequestKeyRef.current = "";
      } catch (error) {
        console.error("Digital Twin meter filter sync error:", error);

        const fallbackFilters = {
          ...DEFAULT_FILTERS,
          mode: "Custom",
          chip: "Custom",
          start: startDateFromDigitalTwin || DEFAULT_FILTERS.start,
          end: endDateFromDigitalTwin || DEFAULT_FILTERS.end,
          meter: [meterIdFromDigitalTwin],
          account: [],
          category: [],
        };

        setFilters(fallbackFilters);
        setOptionFilters({
          account: [],
          meter: [meterIdFromDigitalTwin],
          category: [],
        });
      } finally {
        if (!cancelled) {
          setLoadingOptions(false);
        }
      }
    };

    applyDigitalTwinMeterFilters();

    return () => {
      cancelled = true;
    };
  }, [
    districtId,
    meterIdFromDigitalTwin,
    startDateFromDigitalTwin,
    endDateFromDigitalTwin,
  ]);

  //

  useEffect(() => {
    let cancelled = false;

    const loadInitialOptions = async () => {
      if (!districtId) return;

      try {
        setLoadingOptions(true);

        const response = await getAccountMeterCategoryList({
          accounts: [],
          meter_ids: [],
          codes: [],
          meter_status: draftMeterStatus,
        });

        if (cancelled) return;

        const apiData = getDropdownApiData(response);

        const initialOptions = {
          accounts: mapAccounts(apiData.accounts || []),
          meters: uniqueByKey(
            [
              ...(meterIdFromDigitalTwin
                ? [{ id: meterIdFromDigitalTwin, name: meterIdFromDigitalTwin }]
                : []),
              ...mapMeters(apiData.meters || []),
            ],
            "id",
          ),
          categories: mapCategories(apiData.codes || []),
        };

        allDropdownOptionsRef.current = initialOptions;

        setOptions(initialOptions);
      } catch (error) {
        console.error("Initial dropdown API error:", error);

        setOptions({
          accounts: [],
          meters: [],
          categories: [],
        });
      } finally {
        if (!cancelled) {
          setLoadingOptions(false);
        }
      }
    };

    loadInitialOptions();

    return () => {
      cancelled = true;
    };
  }, [districtId, draftMeterStatus, meterIdFromDigitalTwin]);

  const handleOptionFilterChange = async (nextFilters, changedBy) => {
    if (!districtId) return nextFilters;

    try {
      setLoadingOptions(true);

      const finalFilters = await fetchDropdownOptions(nextFilters, changedBy);

      return finalFilters;
    } catch (error) {
      console.error("Dropdown filter API error:", error);

      return nextFilters;
    } finally {
      setLoadingOptions(false);
    }
  };

  // end dropdown options

  // KPI

  const getKpiOption = (filters) => {
    if (shouldSendAsCustom(filters)) {
      return "Custom";
    }

    switch (filters.chip) {
      case "D":
        return "Past Day";
      case "W":
        return "Past Week";
      case "1M":
        return "Past Month";
      case "3M":
        return "Past 3 Months";
      case "6M":
        return "Past 6 Months";
      case "1Y":
        return "Past Year";
      default:
        return "Past Month";
    }
  };

  const mapWaterUsageKpiResponse = (response) => {
    const apiData = response?.data || response || {};

    const currentKpi = apiData?.kpi?.[0] || {};
    const deltaPct = apiData?.comparison?.deltaPct || {};

    return {
      mode: "district",

      supply: {
        value: currentKpi.supply ?? null,
        deltaPct: deltaPct.supply ?? null,
      },

      demand: {
        value: currentKpi.flow ?? null,
        deltaPct: deltaPct.flow ?? null,
      },

      loss: {
        value: currentKpi.loss ?? null,
        deltaPct: deltaPct.loss ?? null,
      },

      avgDaily: {
        value: currentKpi.avg_flow ?? null,
        deltaPct: deltaPct.avg_flow ?? null,
      },

      peak: {
        value: currentKpi.peak ?? null,
        deltaPct: deltaPct.peak ?? null,
        dateLabel: currentKpi.peak_flow_time || "",
      },
    };
  };

  useEffect(() => {
    const fetchKPIs = async () => {
      console.log(selectedDistrict, "selectedDistrict");
      const requestKey = getFilterKey(filters, districtId, appliedMeterStatus);

      if (kpiRequestKeyRef.current === requestKey) {
        return;
      }

      kpiRequestKeyRef.current = requestKey;

      try {
        setLoadingKpis(true);

        const option = getKpiOption(filters);

        if (option === "Custom" && (!filters.start || !filters.end)) {
          setKpiData(null);
          setKpiMatrix([]);
          return;
        }

        const apiPayload = {
          option,
          interval: filters.interval || "Day",
          meter_ids: filters.meter || [],
          codes: filters.category || [],
          account_ids: filters.account || [],
          meter_status: appliedMeterStatus,
        };

        if (option === "Custom") {
          apiPayload.start_date = filters.start;
          apiPayload.end_date = filters.end;
        }

        const data = await getWaterUsageKPI(apiPayload);

        const apiData = data?.data || data || {};

        setKpiData(mapWaterUsageKpiResponse(data));
        setKpiMatrix(apiData?.kpi_matrix || []);
      } catch (error) {
        console.error("KPI API Error:", error.response?.data || error.message);
        setKpiData(null);
        setKpiMatrix([]);
      } finally {
        setLoadingKpis(false);
      }
    };

    fetchKPIs();
  }, [
    districtId,
    appliedMeterStatus,
    filters.mode,
    filters.chip,
    filters.start,
    filters.end,
    filters.interval,
    filters.meter,
    filters.category,
    filters.account,
    selectedDistrict,
  ]);

  // useEffect(() => {
  //   const fetchRankings = async () => {
  //     if (!districtId) return;

  //     const option = getKpiOption(filters);

  //     if (option === "Custom" && (!filters.start || !filters.end)) {
  //       setMeterRankings([]);
  //       setMeterByFlowMatrix([]);
  //       return;
  //     }

  //     const requestKey = JSON.stringify({
  //       districtId,
  //       meter_status: appliedMeterStatus,
  //       option,
  //       start: filters.start,
  //       end: filters.end,
  //       interval: filters.interval,
  //       account: filters.account || [],
  //       meter: filters.meter || [],
  //       category: filters.category || [],
  //       api: "meter_by_flow",
  //     });

  //     if (rankingRequestKeyRef.current === requestKey) {
  //       return;
  //     }

  //     rankingRequestKeyRef.current = requestKey;

  //     try {
  //       setLoadingRankings(true);

  //       const apiPayload = {
  //         option,
  //         interval: filters.interval || "Day",
  //         meter_ids: filters.meter || [],
  //         codes: filters.category || [],
  //         account_ids: filters.account || [],
  //         accounts: filters.account || [],
  //         meter_status: appliedMeterStatus,
  //       };

  //       if (option === "Custom") {
  //         apiPayload.start_date = filters.start;
  //         apiPayload.end_date = filters.end;
  //       }

  //       const res = await getMetersByFlow(apiPayload);

  //       const responseData = res?.data || res || {};
  //       const apiData = responseData?.data || responseData;

  //       const rankings = Array.isArray(apiData)
  //         ? apiData
  //         : apiData?.meters_by_flow ||
  //           apiData?.meter_by_flow ||
  //           responseData?.meters_by_flow ||
  //           responseData?.meter_by_flow ||
  //           [];

  //       const apiMatrix =
  //         responseData?.meter_by_flow_matrix ||
  //         responseData?.meters_by_flow_matrix ||
  //         apiData?.meter_by_flow_matrix ||
  //         apiData?.meters_by_flow_matrix ||
  //         [];

  //       const fallbackMatrix = [
  //         [
  //           "Meter ID",
  //           "Account Name",
  //           "Category",
  //           "Total Usage",
  //           "Last Comm Time",
  //         ],
  //         ...(Array.isArray(rankings) ? rankings : []).map((item) => [
  //           item.meter || item.meter_id || "",
  //           item.account_name || "",
  //           item.code_name || item.category || "",
  //           item.flow || 0,
  //           item.last_comm_time || "",
  //         ]),
  //       ];

  //       setMeterRankings(Array.isArray(rankings) ? rankings : []);

  //       setMeterByFlowMatrix(
  //         Array.isArray(apiMatrix) && apiMatrix.length > 0
  //           ? apiMatrix
  //           : fallbackMatrix,
  //       );
  //     } catch (error) {
  //       console.error(
  //         "Ranked Meters API Error:",
  //         error.response?.data || error.message,
  //       );

  //       setMeterRankings([]);
  //       setMeterByFlowMatrix([]);
  //     } finally {
  //       setLoadingRankings(false);
  //     }
  //   };

  //   fetchRankings();
  // }, [
  //   districtId,
  //   appliedMeterStatus,
  //   filters.mode,
  //   filters.chip,
  //   filters.start,
  //   filters.end,
  //   filters.interval,
  //   filters.account,
  //   filters.meter,
  //   filters.category,
  // ]);
  useEffect(() => {
    const fetchRankings = async () => {
      if (!districtId) return;

      const option = getKpiOption(filters);

      if (option === "Custom" && (!filters.start || !filters.end)) {
        setMeterRankings([]);
        setMeterByFlowMatrix([]);
        return;
      }

      // Append parameter tracking to the internal request guard key
      const requestKey = JSON.stringify({
        districtId,
        meter_status: appliedMeterStatus,
        option,
        start: filters.start,
        end: filters.end,
        interval: filters.interval,
        account: filters.account || [],
        meter: filters.meter || [],
        category: filters.category || [],
        api: "meter_by_flow",
        meter_all: showMeterAllToggle ? meterAll : undefined, // <-- Added tracking
      });

      if (rankingRequestKeyRef.current === requestKey) {
        return;
      }

      rankingRequestKeyRef.current = requestKey;

      try {
        setLoadingRankings(true);

        const apiPayload = {
          option,
          interval: filters.interval || "Day",
          meter_ids: filters.meter || [],
          codes: filters.category || [],
          account_ids: filters.account || [],
          accounts: filters.account || [],
          meter_status: appliedMeterStatus,
        };

        // Conditionally attach the parameter to the API payload
        if (showMeterAllToggle) {
          apiPayload.meter_all = meterAll;
        }

        if (option === "Custom") {
          apiPayload.start_date = filters.start;
          apiPayload.end_date = filters.end;
        }

        const res = await getMetersByFlow(apiPayload);

        const responseData = res?.data || res || {};
        const apiData = responseData?.data || responseData;

        const rankings = Array.isArray(apiData)
          ? apiData
          : apiData?.meters_by_flow ||
            apiData?.meter_by_flow ||
            responseData?.meters_by_flow ||
            responseData?.meter_by_flow ||
            [];

        const apiMatrix =
          responseData?.meter_by_flow_matrix ||
          responseData?.meters_by_flow_matrix ||
          apiData?.meter_by_flow_matrix ||
          apiData?.meters_by_flow_matrix ||
          [];

        const fallbackMatrix = [
          [
            "Meter ID",
            "Account Name",
            "Category",
            "Total Usage",
            "Last Comm Time",
          ],
          ...(Array.isArray(rankings) ? rankings : []).map((item) => [
            item.meter || item.meter_id || "",
            item.account_name || "",
            item.code_name || item.category || "",
            item.flow || 0,
            item.last_comm_time || "",
          ]),
        ];

        setMeterRankings(Array.isArray(rankings) ? rankings : []);
        setMeterByFlowMatrix(
          Array.isArray(apiMatrix) && apiMatrix.length > 0
            ? apiMatrix
            : fallbackMatrix,
        );
      } catch (error) {
        console.error(
          "Ranked Meters API Error:",
          error.response?.data || error.message,
        );
        setMeterRankings([]);
        setMeterByFlowMatrix([]);
      } finally {
        setLoadingRankings(false);
      }
    };

    fetchRankings();
  }, [
    districtId,
    appliedMeterStatus,
    filters.mode,
    filters.chip,
    filters.start,
    filters.end,
    filters.interval,
    filters.account,
    filters.meter,
    filters.category,
    meterAll, // <-- Added dependency
    showMeterAllToggle, // <-- Added dependency
  ]);
  const normalizeBreakdownItems = (items = [], type = "category") => {
    return (
      safeArray(items)
        .map((item) => {
          const actualValue = Number(item.flow ?? 0);
          const apiPercentage = Number(item.percentage ?? 0);

          // Apex donut cannot render visible slice for 0.
          // Keep actual value as 0, but use chartValue only for drawing.
          const chartValue =
            actualValue > 0
              ? actualValue
              : apiPercentage > 0
                ? apiPercentage
                : 1;

          if (type === "meter") {
            return {
              name: item.meter || item.meter_id || "Unknown Meter",
              value: actualValue,
              chartValue,
              sharePct: item.percentage ?? 0,
              categoryName: item.code_name || "",
              accountName: item.account_name || "",
              meterName: item.meter || item.meter_id || "",
              isZeroFlow: actualValue <= 0,
            };
          }

          if (type === "account") {
            return {
              name: item.account_name || "Unknown Account",
              value: actualValue,
              chartValue,
              sharePct: item.percentage ?? 0,
              categoryName: "",
              accountName: item.account_name || "",
              meterName: "",
              isZeroFlow: actualValue <= 0,
            };
          }

          return {
            name: item.code_name || "Unknown Category",
            value: actualValue,
            chartValue,
            sharePct: item.percentage ?? 0,
            categoryName: item.code_name || "",
            accountName: "",
            meterName: "",
            isZeroFlow: actualValue <= 0,
          };
        })
        // Keep zero-flow API rows also.
        // Only remove invalid/empty name rows.
        .filter((item) => item.name)
    );
  };

  const calculatePercentagesIfMissing = (items = []) => {
    const total = items.reduce((sum, item) => sum + Number(item.value || 0), 0);

    return items.map((item) => ({
      ...item,
      sharePct:
        item.sharePct > 0
          ? item.sharePct
          : total > 0
            ? (Number(item.value || 0) / total) * 100
            : 0,
    }));
  };

  const safeArray = (value) => {
    return Array.isArray(value) ? value : [];
  };

  const hasData = (items = []) => {
    return Array.isArray(items) && items.length > 0;
  };

  const getDefaultBreakdownView = ({
    mappedMeterBreakdown = [],
    mappedAccountBreakdown = [],
    mappedCategoryBreakdown = [],
  }) => {
    // If meter data exists, show meter first
    if (hasData(mappedMeterBreakdown)) return "meter";

    // If account data exists, show account first
    if (hasData(mappedAccountBreakdown)) return "account";

    // If category data exists, show category first
    if (hasData(mappedCategoryBreakdown)) return "category";

    return "category";
  };

  const getAvailableBreakdownViews = () => {
    return {
      category: hasData(breakdownData.categoryBreakdown),
      meter: hasData(breakdownData.meterBreakdown),
      account: hasData(breakdownData.accountBreakdown),
    };
  };

  useEffect(() => {
    const fetchUsageBreakdown = async () => {
      if (!districtId) return;

      const option = getKpiOption(filters);

      if (option === "Custom" && (!filters.start || !filters.end)) {
        setBreakdownData({
          topFlowTitle: "Top Accounts — Total Flow In Range",
          categoryBreakdown: [],
          meterBreakdown: [],
          accountBreakdown: [],
          liveTopAccounts: [],
          totalFlowGallons: 0,
        });

        setBreakdownMatrix({
          breakdown: [],
          accountFlow: [],
        });

        return;
      }

      const requestKey = getFilterKey(filters, districtId);

      if (breakdownRequestKeyRef.current === requestKey) {
        return;
      }

      breakdownRequestKeyRef.current = requestKey;

      try {
        setLoadingBreakdown(true);
        setActiveCategory(null);

        const apiPayload = {
          option,
          interval: filters.interval || "Day",
          meter_ids: filters.meter || [],
          codes: filters.category || [],
          account_ids: filters.account || [],
          accounts: filters.account || [],
          meter_status: appliedMeterStatus,
        };

        if (option === "Custom") {
          apiPayload.start_date = filters.start;
          apiPayload.end_date = filters.end;
        }

        const res = await getWaterUsageBreakdown(apiPayload);

        const apiData = res?.data?.data || res?.data || res || {};

        const codeBreakdown = safeArray(apiData.code_breakdown);
        const meterBreakdown = safeArray(apiData.meter_breakdown);
        const accountBreakdown = safeArray(apiData.account_breakdown);
        const accountFlow = safeArray(apiData.account_flow);

        const mappedCategoryBreakdown = calculatePercentagesIfMissing(
          normalizeBreakdownItems(codeBreakdown, "category"),
        );

        const mappedMeterBreakdown = calculatePercentagesIfMissing(
          normalizeBreakdownItems(meterBreakdown, "meter"),
        );

        const mappedAccountBreakdown = calculatePercentagesIfMissing(
          normalizeBreakdownItems(accountBreakdown, "account"),
        );

        const defaultBreakdownView = getDefaultBreakdownView({
          mappedMeterBreakdown,
          mappedAccountBreakdown,
          mappedCategoryBreakdown,
        });

        setBreakdownView(defaultBreakdownView);

        const mappedTopFlowEntities = accountFlow.map((item) => ({
          name: item.account_name || "Unknown",
          value: Number(item.flow || 0),
        }));

        const totalFlowGallons =
          mappedCategoryBreakdown.length > 0
            ? mappedCategoryBreakdown.reduce(
                (sum, item) => sum + Number(item.value || 0),
                0,
              )
            : accountFlow.reduce(
                (sum, item) => sum + Number(item.flow || 0),
                0,
              );

        setBreakdownData({
          topFlowTitle: "Top Accounts — Total Flow In Range",
          categoryBreakdown: mappedCategoryBreakdown,
          meterBreakdown: mappedMeterBreakdown,
          accountBreakdown: mappedAccountBreakdown,
          liveTopAccounts: mappedTopFlowEntities,
          totalFlowGallons,
        });

        setBreakdownMatrix({
          breakdown: apiData.breakdown_matrix || [],
          accountFlow: apiData.account_flow_matrix || [],
        });
      } catch (error) {
        console.error(
          "Usage Breakdown API Error:",
          error.response?.data || error.message,
        );

        setBreakdownData({
          topFlowTitle: "Top Accounts — Total Flow In Range",
          categoryBreakdown: [],
          meterBreakdown: [],
          accountBreakdown: [],
          liveTopAccounts: [],
          totalFlowGallons: 0,
        });

        setBreakdownMatrix({
          breakdown: [],
          accountFlow: [],
        });
      } finally {
        setLoadingBreakdown(false);
      }
    };

    fetchUsageBreakdown();
  }, [
    districtId,
    appliedMeterStatus,
    filters.mode,
    filters.chip,
    filters.start,
    filters.end,
    filters.interval,
    filters.meter,
    filters.category,
    filters.account,
  ]);

  // =========================
  // RENDER
  // =========================

  const handleMeterLinkClick = (event, row) => {
    event.stopPropagation();

    const meterId = row.meter || row.meter_id;

    if (!meterId) return;

    const category =
      row.code ||
      row.category ||
      row.code_name ||
      filters.category?.[0] ||
      "ALL";

    const selectedDate = filters.start || new Date().toISOString().slice(0, 10);

    navigate(
      `/meter-leak-details?category=${encodeURIComponent(
        category,
      )}&meter_id=${encodeURIComponent(
        meterId,
      )}&time_range=Current&selected_time=${encodeURIComponent(selectedDate)}`,
      {
        state: {
          from: "water-usage",
          meter_id: meterId,
          account_name: row.account_name,
          category,
          flow: row.flow,
        },
      },
    );
  };

  // hourly, weekly, daily trend plot CSV/PNG download handlers - pass down to SectionHeader and ChartTypeToggle

  const downloadMatrixCSV = (matrix = [], fileName = "download.csv") => {
    if (!matrix || matrix.length === 0) {
      return;
    }

    const actualMatrix = Array.isArray(matrix?.[0]?.[0]) ? matrix[0] : matrix;

    const csvContent = actualMatrix
      .map((row) =>
        row
          .map((cell) => {
            const value = cell ?? "";
            const escaped = String(value).replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const downloadDailyAverageCSV = () => {
    downloadMatrixCSV(trendAnalyticsMatrix.daily, "daily-average-trends.csv");
  };

  const downloadHourlyAverageCSV = () => {
    downloadMatrixCSV(trendAnalyticsMatrix.hourly, "hourly-average-trends.csv");
  };

  
  const getWeeklyTrendExportMatrix = () => {
    // 1. Define the table headers
    const headers = [
      "Week Name / Range",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    // 2. Map the regular weekly trend rows
    const regularRows = chartData.weeklyTrend.map((w) => {
      const label = w.week_place ? `${w.name} (${w.week_place})` : w.name;
      return [label, ...w.data];
    });

    // 3. Map the historical trend rows
    const historicalRows = weeklyHistorical.map((w) => {
      const label = w.week_place ? `${w.name} (${w.week_place})` : w.name;
      return [label, ...w.data];
    });

    // 4. Map the newly added custom weeks
    const customRows = customWeeklySeries.map((w) => {
      // Custom series just use the name you passed when creating it
      return [w.name, ...w.data]; 
    });

    // 5. Merge them all together into a unified matrix array
    return [headers, ...regularRows, ...historicalRows, ...customRows];
  };

  const downloadWeeklyTrendCSV = () => {
    downloadMatrixCSV(getWeeklyTrendExportMatrix(), "weekly-trends.csv");
  };

  // all download
  const normalizeMatrix = (matrix = []) => {
    if (!Array.isArray(matrix) || matrix.length === 0) {
      return [];
    }

    // Handles cases like [[[...]]] also
    return Array.isArray(matrix?.[0]?.[0]) ? matrix[0] : matrix;
  };

  const addSheetFromMatrix = (workbook, sheetName, matrix = []) => {
    const finalMatrix = normalizeMatrix(matrix);

    if (!Array.isArray(finalMatrix) || finalMatrix.length === 0) {
      return false;
    }

    const worksheet = XLSX.utils.aoa_to_sheet(finalMatrix);

    // Auto column width
    const columnWidths = finalMatrix[0]?.map((_, colIndex) => {
      const maxLength = finalMatrix.reduce((max, row) => {
        const cellValue = row?.[colIndex] ?? "";
        return Math.max(max, String(cellValue).length);
      }, 10);

      return {
        wch: Math.min(Math.max(maxLength + 2, 12), 40),
      };
    });

    worksheet["!cols"] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    return true;
  };
  // end all downlaod

  const mapHourlyAverageSeriesByDay = (hourlyRoots = [], selectedDay) => {
    const hourOrder = [
      "12 AM",
      "1 AM",
      "2 AM",
      "3 AM",
      "4 AM",
      "5 AM",
      "6 AM",
      "7 AM",
      "8 AM",
      "9 AM",
      "10 AM",
      "11 AM",
      "12 PM",
      "1 PM",
      "2 PM",
      "3 PM",
      "4 PM",
      "5 PM",
      "6 PM",
      "7 PM",
      "8 PM",
      "9 PM",
      "10 PM",
      "11 PM",
    ];

    const valuesByHour = {};

    hourOrder.forEach((hour) => {
      valuesByHour[hour] = [];
    });

    hourlyRoots.forEach((root) => {
      let rawHourly = [];

      if (selectedDay === "Weekdays") {
        rawHourly = root.weekday_average || [];
      } else if (selectedDay === "Weekends") {
        rawHourly = root.weekend_average || [];
      } else {
        rawHourly = root.by_weekday?.[selectedDay] || [];
      }

      rawHourly.forEach((item) => {
        const hourLabel = item.hour_12;
        const value = Number(item.average_flow || 0);

        if (!hourLabel) return;

        valuesByHour[hourLabel].push(value);
      });
    });

    const points = hourOrder.map((hour) => {
      const values = valuesByHour[hour] || [];

      const averageFlow =
        values.length > 0
          ? values.reduce((sum, value) => sum + value, 0) / values.length
          : 0;

  const actualFlow = Math.round(averageFlow);

return {
  x: hour,
  y: actualFlow,
  actualFlow,
  fillColor:
    selectedDay === "Weekdays" || selectedDay === "Weekends"
      ? "#b8e986"
      : "#2dd4bf",
};
    });

    return {
      points,
      details: {},
      series: [
        {
          name: "Average Flow",
          data: points,
        },
      ],
    };
  };

  const mapDailyAverageSeries = (dailyRoots = []) => {
    const categories = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
      "Weekday",
      "Weekend",
    ];

    const totals = {};
    const details = {};

    categories.forEach((category) => {
      totals[category] = 0;
      details[category] = [];
    });

    dailyRoots.forEach((root) => {
      const accountName = root?.account_name || "Unknown Account";

      const weekdayMap = new Map();

      (root.by_weekday || []).forEach((item) => {
        weekdayMap.set(item.weekday, Number(item.average_daily_flow || 0));
      });

      categories.forEach((category) => {
        let value = 0;

        if (category === "Weekday") {
          value = Number(root.weekday_vs_weekend?.weekday || 0);
        } else if (category === "Weekend") {
          value = Number(root.weekday_vs_weekend?.weekend || 0);
        } else {
          value = Number(weekdayMap.get(category) || 0);
        }

        totals[category] += value;

        if (value > 0) {
          details[category].push({
            name: accountName,
            value,
          });
        }
      });
    });

    const AVG_BAR_COLORS = {
      group: "#38bdf8",
      day: "#2dd4bf",
    };

const points = categories.map((category) => {
  const actualFlow = Number(totals[category] || 0);

  return {
    x: category,
    y: actualFlow,
    actualFlow,
    fillColor: isWeekGroupLabel(category)
      ? AVG_CHART_COLORS.groupBar
      : AVG_CHART_COLORS.dayBar,
  };
});

    return {
      categories,
      points,
      details,
      series: [
        {
          name: "Total Flow",
          data: points,
        },
      ],
    };
  };

  // useEffect(() => {
  //   const fetchTrendAnalytics = async () => {
  //     if (!districtId) return;

  //     const option = getKpiOption(filters);

  //     if (option === "Custom" && (!filters.start || !filters.end)) {
  //       setLoadingTrendAnalytics(false);

  //       setChartData((prev) => ({
  //         ...prev,
  //         dailyAvg: [],
  //         hourlyAvg: [],
  //         weeklyTrend: [],
  //       }));

  //       setDailyAverageSeries([]);
  //       setHourlyAverageSeries([]);
  //       setHourlyAnalyticsRoots([]);

  //       setTrendAnalyticsMatrix({
  //         daily: [],
  //         hourly: [],
  //         weekly: [],
  //       });

  //       return;
  //     }

  //     const requestKey = JSON.stringify({
  //       districtId,
  //       meter_status: appliedMeterStatus,
  //       option,
  //       start: filters.start,
  //       end: filters.end,
  //       interval: filters.interval,
  //       account: filters.account || [],
  //       meter: filters.meter || [],
  //       category: filters.category || [],
  //       api: "trend_analytics",
  //     });

  //     if (trendAnalyticsRequestKeyRef.current === requestKey) {
  //       return;
  //     }

  //     trendAnalyticsRequestKeyRef.current = requestKey;

  //     try {
  //       setLoadingTrendAnalytics(true);

  //       const trendAnalyticsPayload = {
  //         option,
  //         interval: filters.interval || "Day",
  //         meter_ids: filters.meter || [],
  //         codes: filters.category || [],
  //         account_ids: filters.account || [],
  //         accounts: filters.account || [],
  //         meter_status: appliedMeterStatus,
  //       };

  //       if (option === "Custom") {
  //         trendAnalyticsPayload.start_date = filters.start;
  //         trendAnalyticsPayload.end_date = filters.end;
  //       }

  //       const res = await getDailyHourlyTrendData(trendAnalyticsPayload);

  //       const apiData = res?.data || res || {};

  //       const dailyRoots = apiData.daily_average_trends || [];
  //       const hourlyRoots = apiData.hourly_average_trends || [];
  //       const weeklyRoots = apiData?.weekly_trends || [];

  //       setHourlyAnalyticsRoots(hourlyRoots);

  //       const mappedDaily = mapDailyAverageSeries(dailyRoots);
  //       const mappedHourly = mapHourlyAverageSeriesByDay(
  //         hourlyRoots,
  //         hourlyDay,
  //       );

  //       const dailyAvg = mappedDaily.points;
  //       const hourlyAvg = mappedHourly.points;

  //       setDailyAverageSeries(mappedDaily.series);
  //       setHourlyAverageSeries(mappedHourly.series);

  //       setDailyAverageDetails(mappedDaily.details);
  //       setHourlyAverageDetails(mappedHourly.details);

  //       const dayMap = {
  //         Monday: 0,
  //         Tuesday: 1,
  //         Wednesday: 2,
  //         Thursday: 3,
  //         Friday: 4,
  //         Saturday: 5,
  //         Sunday: 6,
  //       };

  //       const weeklyTrendMap = new Map();

  //       weeklyRoots.forEach((weeklyRoot) => {
  //         (weeklyRoot.daily || []).forEach((week) => {
  //           const weekRange = week.week_range || "Unknown Week";

  //           // Use null for missing days.
  //           // Do NOT use 0, otherwise chart line goes down to zero.
  //           if (!weeklyTrendMap.has(weekRange)) {
  //             weeklyTrendMap.set(weekRange, [
  //               null,
  //               null,
  //               null,
  //               null,
  //               null,
  //               null,
  //               null,
  //             ]);
  //           }

  //           const flows = weeklyTrendMap.get(weekRange);

  //           (week.days || []).forEach((day) => {
  //             const index = dayMap[day.weekday];

  //             if (index !== undefined) {
  //               const value = Number(day.total_flow ?? 0);

  //               // If same week/day comes from multiple roots, add it.
  //               // If it is missing, keep null.
  //               flows[index] =
  //                 flows[index] === null ? value : flows[index] + value;
  //             }
  //           });
  //         });
  //       });

  //       const weeklyTrend = Array.from(weeklyTrendMap.entries())
  //         .map(([weekRange, flows]) => ({
  //           name: weekRange,
  //           data: flows,
  //         }))
  //         // Remove weeks that have no real values
  //         .filter((week) => week.data.some((value) => value !== null));

  //       setChartData((prev) => ({
  //         ...prev,
  //         dailyAvg,
  //         hourlyAvg,
  //         weeklyTrend,
  //       }));

  //       setTrendAnalyticsMatrix({
  //         daily: apiData.daily_average_trends_matrix || [],
  //         hourly: apiData.hourly_average_trends_matrix || [],
  //         weekly: apiData.weekly_trends_matrix || [],
  //       });
  //     } catch (error) {
  //       console.error(
  //         "Trend Analytics API Error:",
  //         error.response?.data || error.message,
  //       );

  //       setChartData((prev) => ({
  //         ...prev,
  //         dailyAvg: [],
  //         hourlyAvg: [],
  //         weeklyTrend: [],
  //       }));

  //       setTrendAnalyticsMatrix({
  //         daily: [],
  //         hourly: [],
  //         weekly: [],
  //       });
  //     } finally {
  //       setLoadingTrendAnalytics(false);
  //     }
  //   };

  //   fetchTrendAnalytics();
  // }, [
  //   districtId,
  //   appliedMeterStatus,
  //   filters.mode,
  //   filters.chip,
  //   filters.start,
  //   filters.end,
  //   filters.interval,
  //   filters.account,
  //   filters.meter,
  //   filters.category,
  // ]);
  useEffect(() => {
    if (!districtId) return;

    const option = getKpiOption(filters);

    // If Custom is selected but dates aren't ready yet, clear out safely
    if (option === "Custom" && (!filters.start || !filters.end)) {
      setLoadingDailyHourly(false);
      setLoadingWeekly(false);
      setChartData((prev) => ({
        ...prev,
        dailyAvg: [],
        hourlyAvg: [],
        weeklyTrend: [],
      }));
      setDailyAverageSeries([]);
      setHourlyAverageSeries([]);
      setHourlyAnalyticsRoots([]);
      return;
    }

    // Standardize key properties to prevent duplicate requests
    const baseKeyObj = {
      districtId,
      meter_status: appliedMeterStatus,
      option,
      start: filters.start,
      end: filters.end,
      interval: filters.interval,
      account: filters.account || [],
      meter: filters.meter || [],
      category: filters.category || [],
    };

    const dailyHourlyKey = JSON.stringify({
      ...baseKeyObj,
      api: "daily_hourly",
    });
    const weeklyKey = JSON.stringify({ ...baseKeyObj, api: "weekly" });

    const apiPayload = {
      option,
      interval: filters.interval || "Day",
      meter_ids: filters.meter || [],
      codes: filters.category || [],
      account_ids: filters.account || [],
      accounts: filters.account || [],
      meter_status: appliedMeterStatus,
    };

    if (option === "Custom") {
      apiPayload.start_date = filters.start;
      apiPayload.end_date = filters.end;
    }

    // Segment A: Daily & Hourly Fetch and Formatting
    const fetchDailyHourlyData = async () => {
      if (dailyHourlyRequestKeyRef.current === dailyHourlyKey) return;
      dailyHourlyRequestKeyRef.current = dailyHourlyKey;

      try {
        setLoadingDailyHourly(true);
        const res = await getDailyHourlyTrendData(apiPayload);
        const apiData = res?.data || res || {};

        const dailyRoots = apiData.daily_average_trends || [];
        const hourlyRoots = apiData.hourly_average_trends || [];

        setHourlyAnalyticsRoots(hourlyRoots);

        const mappedDaily = mapDailyAverageSeries(dailyRoots);
        const mappedHourly = mapHourlyAverageSeriesByDay(
          hourlyRoots,
          hourlyDay,
        );

        setDailyAverageSeries(mappedDaily.series);
        setHourlyAverageSeries(mappedHourly.series);
        setDailyAverageDetails(mappedDaily.details);
        setHourlyAverageDetails(mappedHourly.details);

        setChartData((prev) => ({
          ...prev,
          dailyAvg: mappedDaily.points,
          hourlyAvg: mappedHourly.points,
        }));

        setTrendAnalyticsMatrix((prev) => ({
          ...prev,
          daily: apiData.daily_average_trends_matrix || [],
          hourly: apiData.hourly_average_trends_matrix || [],
        }));
      } catch (error) {
        console.error("Daily/Hourly Analytics Error:", error);
        setChartData((prev) => ({ ...prev, dailyAvg: [], hourlyAvg: [] }));
      } finally {
        setLoadingDailyHourly(false);
      }
    };

    // Inside WaterUsageDetail.jsx -> fetchWeeklyData
    const fetchWeeklyData = async () => {
      if (weeklyRequestKeyRef.current === weeklyKey) return;
      weeklyRequestKeyRef.current = weeklyKey;

      try {
        setLoadingWeekly(true);
        const res = await getWeeklyTrendData(apiPayload);
        const apiData = res?.data || res || {};

        const historicalSeries = (apiData.weekly_trends || []).flatMap((root) =>
          (root.daily || []).map((week) => {
            let seriesName = week.week_range;
            let bracketText = week.week_place; // Grab week_place from API

            // Swap the Name and Bracket Text for Year-over-Year data
            if (week.week_place === "Same Week Last Year") {
              seriesName = "Same Week Last Year";
              bracketText = week.week_range;
            } else if (week.week_place === "Same Week 2 Years Ago") {
              seriesName = "Same Week 2 Years Ago";
              bracketText = week.week_range;
            } else if (
              week.week_place === "4 Yr Peak" ||
              week.week_range === "Historical Max"
            ) {
              seriesName = "Historical Max";
              // If the API sends "4 Yr Peak" for week_place, it will be kept in the bracket
            } else if (week.week_range?.toLowerCase().includes("baseline")) {
              seriesName = week.week_range; // e.g., "5-week Avg Baseline"
              bracketText = week.week_place || "";
            }

            return {
              name: seriesName,
              date_range: week.week_range,
              week_place: bracketText, // Inject bracket text into the series object
              data: week.days.map((day) => Number(day.total_flow || 0)),
            };
          }),
        );

        setChartData((prev) => ({ ...prev, weeklyTrend: historicalSeries }));
        setTrendAnalyticsMatrix((prev) => ({
          ...prev,
          weekly: apiData.weekly_trends_matrix || [],
        }));
      } catch (error) {
        console.error("Weekly Analytics Error:", error);
        setChartData((prev) => ({ ...prev, weeklyTrend: [] }));
      } finally {
        setLoadingWeekly(false);
      }
    };
    fetchDailyHourlyData();
    fetchWeeklyData();
  }, [
    districtId,
    appliedMeterStatus,
    filters.mode,
    filters.chip,
    filters.start,
    filters.end,
    filters.interval,
    filters.account,
    filters.meter,
    filters.category,
  ]);

  useEffect(() => {
    if (!hourlyAnalyticsRoots || hourlyAnalyticsRoots.length === 0) return;

    const mappedHourly = mapHourlyAverageSeriesByDay(
      hourlyAnalyticsRoots,
      hourlyDay,
    );

    setHourlyAverageSeries(mappedHourly.series);
    setHourlyAverageDetails(mappedHourly.details);

    setChartData((prev) => ({
      ...prev,
      hourlyAvg: mappedHourly.points,
    }));
  }, [hourlyDay, hourlyAnalyticsRoots]);

  // PNG Download

  const waitForDownloadRender = () =>
    new Promise((resolve) => {
      requestAnimationFrame(() => {
        setTimeout(resolve, 300);
      });
    });

  const hasUnsupportedColor = (value) => {
    if (!value || typeof value !== "string") return false;

    const lower = value.toLowerCase();

    return (
      lower.includes("oklch(") ||
      lower.includes("lab(") ||
      lower.includes("lch(") ||
      lower.includes("color(")
    );
  };

  const safeColor = (value, fallback) => {
    return hasUnsupportedColor(value) ? fallback : value || fallback;
  };

  const sanitizeColorsOnly = (root) => {
    const elements = [root, ...root.querySelectorAll("*")];

    elements.forEach((el) => {
      const computed = window.getComputedStyle(el);
      const tagName = el.tagName?.toLowerCase();

      // IMPORTANT: do NOT remove class names.
      // Removing classes breaks Tailwind grid/flex/padding layout.

      el.style.color = safeColor(computed.color, "#e6f0ff");

      el.style.backgroundColor = safeColor(
        computed.backgroundColor,
        el === root ? "#0f172a" : "transparent",
      );

      el.style.borderTopColor = safeColor(computed.borderTopColor, "#233d5e");
      el.style.borderRightColor = safeColor(
        computed.borderRightColor,
        "#233d5e",
      );
      el.style.borderBottomColor = safeColor(
        computed.borderBottomColor,
        "#233d5e",
      );
      el.style.borderLeftColor = safeColor(computed.borderLeftColor, "#233d5e");

      el.style.outlineColor = safeColor(computed.outlineColor, "#233d5e");

      if (hasUnsupportedColor(computed.boxShadow)) {
        el.style.boxShadow = "none";
      }

      if (hasUnsupportedColor(computed.textShadow)) {
        el.style.textShadow = "none";
      }

      if (hasUnsupportedColor(computed.backgroundImage)) {
        el.style.backgroundImage = "none";
      }

      // SVG chart shapes
      if (
        [
          "path",
          "line",
          "circle",
          "rect",
          "polygon",
          "polyline",
          "text",
        ].includes(tagName)
      ) {
        const fill = el.getAttribute("fill");
        const stroke = el.getAttribute("stroke");

        if (hasUnsupportedColor(fill)) {
          el.setAttribute("fill", "#2ea8ff");
        }

        if (hasUnsupportedColor(stroke)) {
          el.setAttribute("stroke", "#2ea8ff");
        }

        if (hasUnsupportedColor(el.style.fill)) {
          el.style.fill = "#2ea8ff";
        }

        if (hasUnsupportedColor(el.style.stroke)) {
          el.style.stroke = "#2ea8ff";
        }
      }
    });
  };

  const downloadElementAsPNG = async (elementRef, fileName) => {
    let exportWrapper = null;

    try {
      const element = elementRef.current;

      if (!element) {
        console.error("PNG download failed: element ref is empty");
        return;
      }

      await waitForDownloadRender();

      const clonedElement = element.cloneNode(true);

      sanitizeColorsOnly(clonedElement);

      exportWrapper = document.createElement("div");
      exportWrapper.style.position = "fixed";
      exportWrapper.style.left = "-10000px";
      exportWrapper.style.top = "0";
      exportWrapper.style.width = `${element.offsetWidth}px`;
      exportWrapper.style.backgroundColor = "#0f172a";
      exportWrapper.style.zIndex = "-1";

      clonedElement.style.width = `${element.offsetWidth}px`;
      clonedElement.style.minHeight = `${element.offsetHeight}px`;
      clonedElement.style.backgroundColor = "#0f172a";

      exportWrapper.appendChild(clonedElement);
      document.body.appendChild(exportWrapper);

      const canvas = await html2canvas(clonedElement, {
        backgroundColor: "#0f172a",
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        scrollX: 0,
        scrollY: 0,
        width: element.offsetWidth,
        height: element.offsetHeight,
        ignoreElements: (el) => {
          return (
            el.classList?.contains("apexcharts-toolbar") ||
            el.classList?.contains("ant-dropdown") ||
            el.classList?.contains("ant-dropdown-menu")
          );
        },
      });

      const image = canvas.toDataURL("image/png");

      const link = document.createElement("a");
      link.href = image;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("PNG download error:", error);
      toast.error("PNG download failed. Please try again.");
    } finally {
      if (exportWrapper) {
        document.body.removeChild(exportWrapper);
      }
    }
  };

  // ?download

  const downloadDailyAveragePNG = async () => {
    await downloadElementAsPNG(dailyHourlyChartRef, "daily-average-trends.png");
  };

  const downloadHourlyAveragePNG = async () => {
    await downloadElementAsPNG(
      dailyHourlyChartRef,
      "hourly-average-trends.png",
    );
  };

  const downloadBreakdownCSV = () => {
    const combinedMatrix = [];

    if (breakdownMatrix.breakdown?.length > 0) {
      combinedMatrix.push(["Breakdown Data"]);
      combinedMatrix.push(...breakdownMatrix.breakdown);
      combinedMatrix.push([]);
    }

    if (breakdownMatrix.accountFlow?.length > 0) {
      combinedMatrix.push(["Account Flow Data"]);
      combinedMatrix.push(...breakdownMatrix.accountFlow);
    }

    downloadMatrixCSV(combinedMatrix, "breakdown-and-account-flow.csv");
  };

  const downloadBreakdownPNG = async () => {
    await downloadElementAsPNG(
      breakdownChartRef,
      "breakdown-and-account-flow.png",
    );
  };

  const downloadWeeklyTrendPNG = async () => {
    await downloadElementAsPNG(weeklyChartRef, "weekly-trend.png");
  };

  const getMeterByFlowExportMatrix = () => {
    const fallbackMatrix = [
      ["Meter ID", "Account Name", "Category", "Total Usage", "Last Comm Time"],
      ...sortedMeters.map((item) => [
        item.meter || item.meter_id || "",
        item.account_name || "",
        item.code_name || item.category || "",
        item.flow || 0,
        item.last_comm_time || "",
      ]),
    ];

    return Array.isArray(meterByFlowMatrix) && meterByFlowMatrix.length > 0
      ? meterByFlowMatrix
      : fallbackMatrix;
  };

  const downloadMeterByFlowCSV = () => {
    downloadMatrixCSV(getMeterByFlowExportMatrix(), "meter-by-flow.csv");
  };

  const downloadAllReportsExcel = () => {
    const workbook = XLSX.utils.book_new();

    let hasAnySheet = false;
    hasAnySheet =
      addSheetFromMatrix(workbook, "KPI Summary", kpiMatrix) || hasAnySheet;

    hasAnySheet =
      addSheetFromMatrix(workbook, "Trend Plots", trendPlotsMatrix) ||
      hasAnySheet;

    hasAnySheet =
      addSheetFromMatrix(
        workbook,
        "Daily Average",
        trendAnalyticsMatrix.daily,
      ) || hasAnySheet;

    hasAnySheet =
      addSheetFromMatrix(
        workbook,
        "Hourly Average",
        trendAnalyticsMatrix.hourly,
      ) || hasAnySheet;

    hasAnySheet =
      addSheetFromMatrix(
        workbook,
        "Weekly Trend",
        trendAnalyticsMatrix.weekly,
      ) || hasAnySheet;

    hasAnySheet =
      addSheetFromMatrix(workbook, "Breakdown", breakdownMatrix.breakdown) ||
      hasAnySheet;

    hasAnySheet =
      addSheetFromMatrix(
        workbook,
        "Account Flow",
        breakdownMatrix.accountFlow,
      ) || hasAnySheet;

    hasAnySheet =
      addSheetFromMatrix(
        workbook,
        "Meters By Flow",
        getMeterByFlowExportMatrix(),
      ) || hasAnySheet;

    if (!hasAnySheet) {
      toast.warning("No export data available.");
      return;
    }

    const fileDate = new Date().toISOString().slice(0, 10);

    XLSX.writeFile(workbook, `water-usage-all-reports-${fileDate}.xlsx`);
  };

  const filterPanelValue = useMemo(
    () => ({
      ...filters,

      // Dropdown UI value can change separately.
      // Graph APIs still use `filters`, so graph will not reload until Apply.
      account: optionFilters.account || [],
      meter: optionFilters.meter || [],
      category: optionFilters.category || [],
    }),
    [filters, optionFilters],
  );

  useEffect(() => {
    if (!weeklyChartRef.current) return;

    const timer = setTimeout(() => {
      removeApexDefaultTitle(weeklyChartRef.current);
    }, 300);

    return () => clearTimeout(timer);
  }, [chartData.weeklyTrend]);

  return (
    <div className="water-usage-page p-0">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            {isFromDigitalTwin && (
              <button
                type="button"
                onClick={() => navigate("/schematics")}
                className="h-[38px] px-3 rounded-lg border flex items-center gap-2 text-sm font-semibold transition-all hover:opacity-90"
                style={{
                  borderColor: colors.primary,
                  backgroundColor: `${colors.primary}20`,
                  color: colors.primary,
                }}
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            )}

            <h1
              className="text-4xl font-bold bg-clip-text text-transparent"
              style={{
                backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                lineHeight: "normal",
              }}
            >
              Water Usage
            </h1>
          </div>

          <p className="mt-2" style={{ color: colors.textSecondary }}>
            District-wide consumption across meters, accounts, and categories
          </p>
        </div>

        <div className="flex items-start gap-2 flex-col">
          <span
            className="text-sm font-medium block"
            style={{ color: colors.textSecondary }}
          >
            Status
          </span>

          <select
            value={draftMeterStatus}
            onChange={(e) => {
              const nextStatus = e.target.value;

              setDraftMeterStatus(nextStatus);

              // Only clear dropdown UI selection.
              // Do NOT change `filters`.
              // Do NOT change `appliedMeterStatus`.
              setOptionFilters({
                account: [],
                meter: [],
                category: [],
              });

              setOptions({
                accounts: [],
                meters: [],
                categories: [],
              });

              optionsRequestKeyRef.current = "";
            }}
            className="h-[40px] min-w-[130px] rounded-md border px-3 text-sm outline-none cursor-pointer"
            style={{
              backgroundColor: colors.background,
              borderColor: colors.border,
              color: colors.text,
            }}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {/* <WaterUsageFilters
        accounts={accounts}
        meters={meters}
        categories={CATEGORIES}
        value={filters}
        onChange={setFilters}
        onDownload={() => {}}
      /> */}

      <WaterUsageFilters
        key={`${districtId}-${meterIdFromDigitalTwin || "all"}-${filters.start}-${filters.end}`}
        accounts={options.accounts}
        meters={options.meters}
        categories={options.categories}
        value={filterPanelValue}
        onChange={(nextFilters) => {
          setAppliedMeterStatus(draftMeterStatus);
          setFlowInterval(nextFilters.interval || "Day");
          setFilters(nextFilters);
        }}
        onIntervalChange={(nextInterval) => {
          setFlowInterval(nextInterval);
        }}
        onOptionFilterChange={handleOptionFilterChange}
        onDownload={downloadAllReportsExcel}
      />

      {isFilteredView && (
        <div className="flex items-center gap-3 mt-4 text-[12px]">
          <span style={{ color: colors.textSecondary }}>
            ● Filtered view: {filteredText}
          </span>

          <button
            onClick={clearFilters}
            className="transition-all text-[12px]"
            style={{ color: colors.primary }}
          >
            × Clear
          </button>
        </div>
      )}

      {/* <KPIStrip mode="district" data={activeKpis} /> */}
      {loadingKpis ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mt-4">
          {[1, 2, 3, 4, 5].map((item) => (
            <div
              key={item}
              className="rounded-xl p-4 min-h-[100px] flex items-center justify-center border"
              style={cardStyle}
            >
              <Spin size="large" />
            </div>
          ))}
        </div>
      ) : (
        <KPIStrip mode={kpiData?.mode || "district"} data={kpiData || {}} />
      )}

      {/* Aggregate */}

      <SectionHeader
        // title="District Aggregate Flow"
        title={isFilteredView ? "Flow" : "District Aggregate Flow"}
        // badge="trend plots"
        onDownloadCSV={downloadTrendPlotsCSV}
        onDownloadPNG={downloadTrendPlotsPNG}
      />

      <div
        ref={aggregateChartRef}
        className="rounded-2xl p-5 mb-4 relative border min-h-[360px]"
        style={cardStyle}
      >
        {/* INSIDE GRAPH CARD - TOP RIGHT */}
        <div className="absolute top-2 right-45 z-10">
          <ChartTypeToggle value={aggType} onChange={setAggType} />
        </div>

        {/* {hasAggregateChartData && (
          <div
            className="absolute top-3 left-5 z-10 text-[11px] font-semibold px-3 py-1 rounded-full border pointer-events-none"
            style={{
              color: isAggregateWheelZoomActive
                ? colors.primary
                : colors.textSecondary,
              borderColor: isAggregateWheelZoomActive
                ? colors.primary
                : colors.border,
              backgroundColor: colors.background,
            }}
          >
            {isAggregateWheelZoomActive
              ? "Mouse zoom enabled — click graph to disable"
              : "Click graph to enable mouse zoom"}
          </div>
        )} */}

        {loadingTrendPlots ? (
          <div className="h-[320px] flex items-center justify-center">
            <Spin size="large" />
          </div>
        ) : !hasAggregateChartData ? (
          <div className="h-[320px] flex items-center justify-center text-[#8ba1c7] text-sm font-semibold">
            No aggregate flow data available.
          </div>
        ) : (
          <div
            onClick={() => setIsAggregateWheelZoomActive((prev) => !prev)}
            className={
              isAggregateWheelZoomActive
                ? "cursor-zoom-out"
                : "cursor-crosshair"
            }
          >
            <ReactApexChart
             key={`aggregate-${aggType}-${flowInterval}-${isAggregateWheelZoomActive}-${aggregateCategoryKey}-${displayAggregateSeries.map((s) => s.name).join("-")}`}
              options={aggregateConfig}
              series={displayAggregateSeries}
              type={aggType}
              height={420}
            />
          </div>
        )}
      </div>

      {/* Daily / Hourly */}

      {/* <SectionHeader
        title="Daily / Hourly Average"
        badge="averages"
      >
        <ChartTypeToggle
          value={dhType}
          onChange={setDhType}
        />
      </SectionHeader> */}
      <SectionHeader
        title="Daily / Hourly Average"
        onDownloadCSV={
          dhTab === "daily" ? downloadDailyAverageCSV : downloadHourlyAverageCSV
        }
        onDownloadPNG={
          dhTab === "daily" ? downloadDailyAveragePNG : downloadHourlyAveragePNG
        }
      />

      <div
        className="rounded-2xl p-5 border min-h-[500px]"
        style={cardStyle}
        ref={dailyHourlyChartRef}
      >
        <div className="flex items-center justify-between mb-5">
          {/* LEFT TABS */}
          <div
            className="flex items-center gap-2 rounded-lg p-1 border"
            style={innerCardStyle}
          >
            <button
              onClick={() => setDhTab("daily")}
              className="px-4 py-2 rounded-md text-sm transition-all"
              style={{
                backgroundColor:
                  dhTab === "daily" ? colors.primary : "transparent",
                color:
                  dhTab === "daily"
                    ? colors.buttonText || "#ffffff"
                    : colors.textSecondary,
              }}
            >
              📅 Daily Average
            </button>

            <button
              onClick={() => setDhTab("hourly")}
              className="px-4 py-2 rounded-md text-sm transition-all"
              style={{
                backgroundColor:
                  dhTab === "hourly" ? colors.primary : "transparent",
                color:
                  dhTab === "hourly"
                    ? colors.buttonText || "#ffffff"
                    : colors.textSecondary,
              }}
            >
              ⏱ Hourly Average
            </button>
          </div>

          {/* RIGHT TOGGLE */}
          {dhTab === "daily" ? (
            <ChartTypeToggle value={dailyType} onChange={setDailyType} />
          ) : (
            <ChartTypeToggle value={hourlyType} onChange={setHourlyType} />
          )}
        </div>

        {loadingDailyHourly ? (
          <div className="h-[420px] flex items-center justify-center">
            <Spin size="large" />
          </div>
        ) : dhTab === "daily" ? (
          dailyAverageSeries.length === 0 ? (
            <div className="h-[420px] flex items-center justify-center text-[#8ba1c7] text-sm font-semibold">
              No daily average data available.
            </div>
          ) : (
            <ReactApexChart
              key={`daily-${dailyType}-${dailyAverageSeries.length}-${chartData.dailyAvg.map((p) => p.x).join("-")}`}
              options={{
                ...dailyConfig,
                tooltip: {
                  ...dailyConfig.tooltip,
                  shared: true,
                  intersect: false,
                },
                xaxis: {
                  ...dailyConfig.xaxis,
                  categories: chartData.dailyAvg.map((p) => p.x),
                },
              }}
              series={dailyChartSeries}
              type={dailyType}
              height={420}
            />
          )
        ) : (
          <div className="grid grid-cols-12 gap-6">
            {/* LEFT FILTER PANEL */}
            <div className="col-span-12 lg:col-span-2">
              <div
                className="rounded-xl p-4 min-h-[420px] border"
                style={innerCardStyle}
              >
                <div className="space-y-4">
                  {[
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                    "Weekdays",
                    "Weekends",
                  ].map((day) => {
                    const isGroup = day === "Weekdays" || day === "Weekends";
                    const isActive = hourlyDay === day;

                    return (
                      <label
                        key={day}
                        className="flex items-center gap-3 text-sm font-bold cursor-pointer transition-all"
                        style={{
                          color: isActive
                            ? colors.primary
                            : isGroup
                              ? AVG_CHART_COLORS.groupLabel
                              : colors.textSecondary,
                        }}
                      >
                        <input
                          type="radio"
                          checked={isActive}
                          onChange={() => setHourlyDay(day)}
                          style={{
                            accentColor: colors.primary,
                          }}
                        />
                        {day}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* RIGHT CHART */}
            <div className="col-span-12 lg:col-span-10 relative">
              {hourlyAverageSeries.length === 0 ? (
                <div className="h-[420px] flex items-center justify-center text-[#8ba1c7] text-sm font-semibold">
                  No hourly average data available.
                </div>
              ) : (
                <>
                  {/* <div
                    className="absolute top-3 left-5 z-10 text-[11px] font-semibold px-3 py-1 rounded-full border pointer-events-none"
                    style={{
                      color: isHourlyWheelZoomActive
                        ? colors.primary
                        : colors.textSecondary,
                      borderColor: isHourlyWheelZoomActive
                        ? colors.primary
                        : colors.border,
                      backgroundColor: colors.background,
                    }}
                  >
                    {isHourlyWheelZoomActive
                      ? "Mouse zoom enabled — click graph to disable"
                      : "Click graph to enable mouse zoom"}
                  </div> */}

                  <div
                    onClick={() => setIsHourlyWheelZoomActive((prev) => !prev)}
                    className={
                      isHourlyWheelZoomActive
                        ? "cursor-zoom-out"
                        : "cursor-crosshair"
                    }
                  >
                    <ReactApexChart
                     key={`hourly-${hourlyType}-${hourlyDay}-${isHourlyWheelZoomActive}-${displayHourlyAverageSeries.length}-${chartData.hourlyAvg.map((p) => p.x).join("-")}`}
                      options={{
                        ...hourlyConfig,
                        tooltip: {
                          ...hourlyConfig.tooltip,
                          shared: true,
                          intersect: false,
                        },
                        xaxis: {
                          ...hourlyConfig.xaxis,
                          categories: chartData.hourlyAvg.map((p) => p.x),
                        },
                      }}
                      series={displayHourlyAverageSeries}
                      type={hourlyType}
                      height={420}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Breakdown */}

      {/* <SectionHeader title="Breakdown" badge="by category & account" /> */}
      <SectionHeader
        title="Breakdown"
        onDownloadCSV={downloadBreakdownCSV}
        onDownloadPNG={downloadBreakdownPNG}
      />

      <div
        className="grid grid-cols-1 xl:grid-cols-2 gap-2 items-stretch"
        ref={breakdownChartRef}
      >
        {/* LEFT DONUT CARD */}
        <div className="rounded-xl p-5 h-full border" style={cardStyle}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm" style={mutedTextStyle}>
              {breakdownTitle}
            </div>

            {(() => {
              const availableViews = getAvailableBreakdownViews();

              const viewOptions = [
                availableViews.category
                  ? { value: "category", label: "By Category" }
                  : null,

                availableViews.meter
                  ? { value: "meter", label: "By Meters" }
                  : null,

                availableViews.account
                  ? { value: "account", label: "By Accounts" }
                  : null,
              ].filter(Boolean);

              if (viewOptions.length <= 1) {
                return null;
              }

              return (
                <select
                  value={breakdownView}
                  onChange={(e) => {
                    setBreakdownView(e.target.value);
                    setActiveCategory(null);
                  }}
                  className="rounded-lg px-3 py-1.5 text-sm border outline-none cursor-pointer"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                    color: colors.text,
                  }}
                >
                  {viewOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              );
            })()}
          </div>

          {loadingBreakdown ? (
            <div className="h-[320px] flex items-center justify-center">
              <Spin size="large" />
            </div>
          ) : selectedBreakdownItems.length === 0 ? (
            <div
              className="h-[320px] flex items-center justify-center text-sm font-semibold"
              style={mutedTextStyle}
            >
              No breakdown data available.
            </div>
          ) : (
            <div
              className="relative"
              onMouseEnter={(e) => removeApexDefaultTitle(e.currentTarget)}
              onMouseMove={(e) => removeApexDefaultTitle(e.currentTarget)}
            >
              <ReactApexChart
                key={`breakdown-${breakdownView}-${selectedBreakdownItems.length}`}
                options={{
                  ...categoryConfig,
                  labels: selectedBreakdownItems.map((c) => c.name),
                  colors: selectedBreakdownItems.map((item, index) =>
                    getBreakdownColor(item, index),
                  ),
                }}
                series={selectedBreakdownItems.map((c) =>
                  Number(c.chartValue ?? c.value ?? 0),
                )}
                type="donut"
                height={335}
              />
            </div>
          )}
        </div>

        {/* RIGHT TOP ACCOUNTS CARD */}
        <div className="rounded-xl p-5 h-full border" style={cardStyle}>
          <div className="flex items-start justify-between mb-3">
            <div className="text-sm" style={{ color: colors.textSecondary }}>
              {topFlowTitle || "Top Accounts — Total Flow In Range"}
            </div>

            <ChartTypeToggle value={acctType} onChange={setAcctType} />
          </div>

          {loadingBreakdown ? (
            <div className="h-[310px] flex items-center justify-center">
              <Spin size="large" />
            </div>
          ) : liveTopAccounts.length === 0 ? (
            <div
              className="h-[310px] flex items-center justify-center text-sm font-semibold"
              style={{ color: colors.textSecondary }}
            >
              No top flow data available.
            </div>
          ) : (
            <ReactApexChart
              key={`account-flow-${acctType}-${liveTopAccounts.length}`}
              options={accountConfig}
              series={[
                {
                  name: "Total Flow",
                  data: liveTopAccounts.map((a) => Number(a.value || 0)),
                },
              ]}
              type={acctType}
              height={315}
            />
          )}
        </div>
      </div>
      {/* Weekly */}

      <SectionHeader
        title="Weekly Trend"
        onDownloadCSV={downloadWeeklyTrendCSV}
        onDownloadPNG={downloadWeeklyTrendPNG}
      />

      <div
        ref={weeklyChartRef}
        className="rounded-md p-5 min-h-[430px] border relative"
        style={{
          backgroundColor: colors.cardBg,
          borderColor: colors.border,
        }}
        onMouseEnter={(e) => removeApexDefaultTitle(e.currentTarget)}
        onMouseMove={(e) => removeApexDefaultTitle(e.currentTarget)}
      >
        {/* {!loadingWeekly && chartData.weeklyTrend.length > 0 && (
          <div
            className="absolute top-3 left-5 z-10 text-[11px] font-semibold px-3 py-1 rounded-full border pointer-events-none"
            style={{
              color: isWeeklyWheelZoomActive
                ? colors.primary
                : colors.textSecondary,
              borderColor: isWeeklyWheelZoomActive
                ? colors.primary
                : colors.border,
              backgroundColor: colors.background,
            }}
          >
            {isWeeklyWheelZoomActive
              ? "Mouse zoom enabled — click graph to disable"
              : "Click graph to enable mouse zoom"}
          </div>
        )} */}

        {loadingWeekly ? (
          <div className="h-[360px] flex items-center justify-center">
            <Spin size="large" />
          </div>
        ) : chartData.weeklyTrend.length === 0 ? (
          <div className="h-[360px] flex items-center justify-center text-[#8ba1c7] text-sm font-semibold">
            No weekly trend data available.
          </div>
        ) : (
          <div
            onClick={() => setIsWeeklyWheelZoomActive((prev) => !prev)}
            onMouseEnter={(e) => removeApexDefaultTitle(e.currentTarget)}
            onMouseMove={(e) => removeApexDefaultTitle(e.currentTarget)}
            className={
              isWeeklyWheelZoomActive ? "cursor-zoom-out" : "cursor-crosshair"
            }
          >
            {/* <WeeklyTrendChart
              weeks={[...chartData.weeklyTrend, ...weeklyHistorical]}
              height={360}
              isWheelZoomActive={isWeeklyWheelZoomActive}
            /> */}
            <WeeklyTrendChart
            // Combine all arrays into the weeks prop
            weeks={[...chartData.weeklyTrend, ...weeklyHistorical, ...customWeeklySeries]} 
            height={360}
            isWheelZoomActive={isWeeklyWheelZoomActive}
            onAddCustomWeek={handleAddCustomWeek} // Pass the handler down
          />
          </div>
        )}
      </div>

      {/* Table */}

      <SectionHeader
        title="Meters by Flow"
        onDownloadCSV={downloadMeterByFlowCSV}
      />

      <div
        className="rounded-md p-4 overflow-auto mb-4 border min-h-[360px]"
        style={cardStyle}
      >
        {/* <p className="text-sm mb-4" style={mutedTextStyle}>
          Click any column header to sort. Click a meter or account to filter.
        </p> */}
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="text-sm" style={mutedTextStyle}>
            Search by meter, category, account, last comm, or flow.
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            {showMeterAllToggle && (
              <label
                className="flex items-center gap-3 text-sm font-semibold cursor-pointer select-none"
                style={{ color: colors.text }}
              >
                <div className="relative inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={meterAll}
                    onChange={(e) => setMeterAll(e.target.checked)}
                    className="sr-only"
                  />
                  {/* Container Track */}
                  <div
                    className="h-[22px] w-[42px] rounded-full transition-colors duration-200 ease-in-out border-2 border-transparent"
                    style={{
                      backgroundColor: meterAll
                        ? colors.primary
                        : colors.border || "#cbd5e1",
                    }}
                  >
                    {/* Sliding Knob */}
                    <span
                      className={`inline-block h-[18px] w-[18px] rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out ${
                        meterAll ? "translate-x-[20px]" : "translate-x-0"
                      }`}
                    />
                  </div>
                </div>
                Show All
              </label>
            )}
            <div
              className="flex items-center gap-2 rounded-md border px-3 h-[40px] w-full sm:w-[320px]"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
            >
              <input
                type="text"
                value={meterSearch}
                onChange={(e) => setMeterSearch(e.target.value)}
                placeholder="Search table..."
                className="w-full bg-transparent outline-none text-sm"
                style={{
                  color: colors.text,
                }}
              />

              {meterSearch && (
                <button
                  type="button"
                  onClick={() => setMeterSearch("")}
                  className="text-sm font-bold"
                  style={{ color: colors.textSecondary }}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        {loadingRankings ? (
          <div className="h-[500px] flex items-center justify-center">
            <Spin size="large" />
          </div>
        ) : paginatedMeters.length === 0 ? (
          <div className="h-[500px] flex items-center justify-center text-[#8ba1c7] text-sm font-semibold">
            {meterSearch
              ? "No matching meter flow data found."
              : "No meter flow data available."}
          </div>
        ) : (
          <>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr
                  className="border-b"
                  style={{
                    borderColor: colors.border,
                    backgroundColor: colors.rowAlt2 || colors.background,
                  }}
                >
                  <th
                    className="text-left py-4 px-6 font-semibold text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    #
                  </th>

                  <th
                    onClick={() => onSortClick("meter")}
                    className="text-left py-4 px-6 font-semibold text-sm cursor-pointer"
                    style={{ color: colors.textSecondary }}
                  >
                    <span className="flex items-center gap-2 select-none">
                      Meter
                      <SortIcon columnKey="meter" />
                    </span>
                  </th>

                  <th
                    onClick={() => onSortClick("code_name")}
                    className="text-left py-4 px-6 font-semibold text-sm cursor-pointer"
                    style={{ color: colors.textSecondary }}
                  >
                    <span className="flex items-center gap-2 select-none">
                      Category
                      <SortIcon columnKey="code_name" />
                    </span>
                  </th>

                  <th
                    onClick={() => onSortClick("account_name")}
                    className="text-left py-4 px-6 font-semibold text-sm cursor-pointer"
                    style={{ color: colors.textSecondary }}
                  >
                    <span className="flex items-center gap-2 select-none">
                      Account
                      <SortIcon columnKey="account_name" />
                    </span>
                  </th>

                  <th
                    onClick={() => onSortClick("last_comm_time")}
                    className="text-left py-4 px-6 font-semibold text-sm cursor-pointer"
                    style={{ color: colors.textSecondary }}
                  >
                    <span className="flex items-center gap-2 select-none">
                      Last Comm
                      <SortIcon columnKey="last_comm_time" />
                    </span>
                  </th>

                  <th
                    onClick={() => onSortClick("flow")}
                    className="text-right py-4 px-6 font-semibold text-sm cursor-pointer"
                    style={{ color: colors.textSecondary }}
                  >
                    <span className="flex items-center justify-end gap-2 select-none">
                      Flow (gal)
                      <SortIcon columnKey="flow" />
                    </span>
                  </th>
                </tr>
              </thead>

              <tbody>
                {paginatedMeters.map((m, i) => (
                  <tr
                    key={`${m.meter || m.meter_id}-${i}`}
                    className="border-b transition-colors"
                    style={{
                      borderColor: colors.border,
                      backgroundColor: colors.cardBg,
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = colors.hoverBg)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = colors.cardBg)
                    }
                  >
                    <td
                      className="py-4 px-6 text-sm"
                      style={{ color: colors.text }}
                    >
                      {meterStartIndex + i + 1}
                    </td>

                    <td className="py-4 px-6 text-sm">
                      <button
                        type="button"
                        onClick={(event) => handleMeterLinkClick(event, m)}
                        className="font-bold underline decoration-1 underline-offset-4 cursor-pointer"
                        style={{
                          color: colors.primary,
                          background: "transparent",
                          border: "none",
                          padding: 0,
                        }}
                      >
                        {m.meter || m.meter_id}
                      </button>
                    </td>

                    <td
                      className="py-4 px-6 text-sm"
                      style={{ color: colors.text }}
                    >
                      {m.code_name || "-"}
                    </td>

                    <td
                      className="py-4 px-6 text-sm cursor-pointer"
                      style={{ color: colors.primary }}
                    >
                      {m.account_name || "-"}
                    </td>

                    <td
                      className="py-4 px-6 text-sm"
                      style={{ color: colors.text }}
                    >
                      {m.last_comm_time || "-"}
                    </td>

                    <td
                      className="py-4 px-6 text-sm text-right font-semibold"
                      style={{ color: colors.primary }}
                    >
                      {Number(m.flow || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {meterTotalPages > 0 && (
              <div
                className="flex items-center justify-between p-4 border-t flex-wrap gap-4"
                style={{
                  borderColor: colors.border,
                  backgroundColor: colors.cardBg,
                }}
              >
                <div className="text-xs" style={mutedTextStyle}>
                  Showing {meterStartIndex + 1} to{" "}
                  {Math.min(
                    meterStartIndex + meterPageSize,
                    sortedMeters.length,
                  )}{" "}
                  of {sortedMeters.length} records
                </div>

                <div className="flex items-center gap-6 flex-wrap">
                  <div className="flex items-center gap-2">
                    {/* PREVIOUS */}
                    <button
                      disabled={meterPage === 1}
                      onClick={() => setMeterPage((p) => Math.max(p - 1, 1))}
                      className="p-1.5 rounded-md border transition-all"
                      style={{
                        borderColor:
                          meterPage === 1 ? colors.border : colors.primary,
                        color:
                          meterPage === 1
                            ? colors.textSecondary
                            : colors.primary,
                        backgroundColor: colors.background,
                        opacity: meterPage === 1 ? 0.45 : 1,
                        cursor: meterPage === 1 ? "not-allowed" : "pointer",
                      }}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>

                    <div className="flex items-center gap-2 px-2">
                      <span className="text-sm" style={mutedTextStyle}>
                        Page
                      </span>

                      <select
                        value={meterPage}
                        onChange={(e) => setMeterPage(Number(e.target.value))}
                        className="text-sm rounded-md px-2 py-1 outline-none border cursor-pointer"
                        style={{
                          backgroundColor: colors.background,
                          borderColor: colors.border,
                          color: colors.text,
                        }}
                      >
                        {Array.from(
                          { length: meterTotalPages },
                          (_, i) => i + 1,
                        ).map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>

                      <span className="text-sm" style={mutedTextStyle}>
                        of {meterTotalPages}
                      </span>
                    </div>

                    {/* NEXT */}
                    <button
                      disabled={meterPage === meterTotalPages}
                      onClick={() =>
                        setMeterPage((p) => Math.min(p + 1, meterTotalPages))
                      }
                      className="p-1.5 rounded-md border transition-all"
                      style={{
                        borderColor:
                          meterPage === meterTotalPages
                            ? colors.border
                            : colors.primary,
                        color:
                          meterPage === meterTotalPages
                            ? colors.textSecondary
                            : colors.primary,
                        backgroundColor: colors.background,
                        opacity: meterPage === meterTotalPages ? 0.45 : 1,
                        cursor:
                          meterPage === meterTotalPages
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>

                  <div
                    className="flex items-center gap-2 text-sm"
                    style={mutedTextStyle}
                  >
                    <span>Rows:</span>

                    <select
                      value={meterPageSize}
                      onChange={(e) => {
                        setMeterPageSize(Number(e.target.value));
                        setMeterPage(1);
                      }}
                      className="rounded px-2 py-1 text-sm border outline-none cursor-pointer"
                      style={{
                        backgroundColor: colors.background,
                        borderColor: colors.border,
                        color: colors.text,
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// =========================
// HELPERS
// =========================

function SectionHeader({
  title,
  badge,
  children,
  onDownloadCSV,
  onDownloadPNG,
}) {
  const { colors } = useTheme();

  const exportItems = [
    ...(onDownloadPNG
      ? [
          {
            key: "png",
            label: (
              <button
                type="button"
                onClick={onDownloadPNG}
                className="w-full text-left"
              >
                Download As PNG
              </button>
            ),
          },
        ]
      : []),

    ...(onDownloadPNG && onDownloadCSV
      ? [
          {
            type: "divider",
          },
        ]
      : []),

    ...(onDownloadCSV
      ? [
          {
            key: "csv",
            label: (
              <button
                type="button"
                onClick={onDownloadCSV}
                className="w-full text-left"
              >
                Download As CSV
              </button>
            ),
          },
        ]
      : []),
  ];

  const hasDownload = Boolean(onDownloadCSV || onDownloadPNG);

  return (
    <div className="flex items-center justify-between mb-3 mt-8">
      <div className="flex items-center gap-3">
        <h2
          className="text-[16px] font-semibold"
          style={{ color: colors.text }}
        >
          {title}
        </h2>

        {badge && (
          <span
            className="px-3 py-1 rounded-full text-[11px] border"
            style={{
              backgroundColor: `${colors.primary}15`,
              color: colors.primary,
              borderColor: `${colors.primary}40`,
            }}
          >
            {badge}
          </span>
        )}
      </div>

      <div className="flex items-center gap-3">
        {hasDownload && (
          <Dropdown
            menu={{ items: exportItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <button
              type="button"
              className="flex items-center justify-center w-[42px] h-[42px] rounded-lg hover:shadow-lg hover:shadow-[#00b8f1]/30 transition-all duration-300"
              style={{
                background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
                color: colors.buttonText || "#ffffff",
              }}
            >
              <Download className="w-5 h-5 text-white" />
            </button>
          </Dropdown>
        )}

        {children}
      </div>
    </div>
  );
}

function ChartTypeToggle({ value, onChange }) {
  const { colors } = useTheme();

  return (
    <div
      className="flex items-center rounded-lg overflow-hidden h-[34px] border"
      style={{
        backgroundColor: colors.background,
        borderColor: colors.border,
      }}
    >
      <button
        onClick={() => onChange("bar")}
        className="px-4 h-full text-sm font-medium transition-all"
        style={{
          backgroundColor: value === "bar" ? colors.primary : "transparent",
          color:
            value === "bar"
              ? colors.buttonText || "#ffffff"
              : colors.textSecondary,
        }}
      >
        <BarChart3 size={18} />
      </button>

      <button
        onClick={() => onChange("line")}
        className="px-4 h-full text-sm font-medium transition-all"
        style={{
          backgroundColor: value === "line" ? colors.primary : "transparent",
          color:
            value === "line"
              ? colors.buttonText || "#ffffff"
              : colors.textSecondary,
        }}
      >
        <Activity size={18} />
      </button>
    </div>
  );
}
export default WaterUsageDetail;
